import {makeAutoObservable, runInAction} from 'mobx';
import {useEffect, useState} from 'react';
import {getStore, useStore} from "./index";
import {createId, parseParamsToObject, timeout} from '../editor/utils'
import axios from "axios";
import uuid from "react-uuid";
import {HTMLToJSON, JSONToHTML} from "html-to-json-parser";
import {toCSS, toJSON} from "css-convert-json"
import Queue from "../editor/utils/queue";
import {
    AddMultiStack,
    AddStack,
    ConvertImageStack,
    getUndoManager,
    GroupingClipboardsStack,
    TableUpdateStack,
    UngroupClipboardsStack,
    UnlockCommonBoardStack
} from "./UndoManager";
import {findCustomCmp, findOneDepthParent, findParent} from "../editor";
import moment from "moment";
import {prettify} from "htmlfy";
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {getUserStore} from "./UserStore";
import {getSocketStore} from "./SocketStore";
/**
 * @typedef {object} PageInfo
 * @property {string} id
 * @property {string} file
 * @property {string} title
 * @property {string[]} commonStyles
 * @property {string[]} commonScripts
 * @property {string[]} assets
 * @property {string[]} scripts
 * @property {string?} status
 * @property {string?} productNo
 * @property {string?} parentPath
 * @property {string?} reserveId
 * @property {string?} reserveTime
 */
/**
 * @typedef {object} ConnectedBoardInfo
 * @property {string?} url
 * @property {string} name
 * @property {string} type
 * @property {number?} commentCount
 * @property {string[]} scripts
 * @property {string[]} commonScripts
 * @property {string[]} commonStyles
 * @property {string?} commonBoardOrigin
 * @property {string} content
 * @property {string} style
 */
/**
 * @typedef {object} BoardCommentMap
 * @property {string} selector
 * @property {string} boardType
 */
/**
 * @typedef {object} LayerHoverView
 * @property {string} css
 * @property {string[]} commonStyles
 * @property {string[]} commonScripts
 * @property {string} html
 * @property {boolean} state
 * @property {string[]} scripts
 */
/**
 * @typedef AspectRatio
 * @property {number} width 가로 비율
 * @property {number} height 세로 비율
 *
 * @typedef CommonBoardRatio
 * @property {AspectRatio} Desktop pc 버전 비율
 * @property {AspectRatio} Mobile mo 버전 비율
 */
/**
 * @typedef {object} CommonBoardEditorModal
 * @property {boolean} state
 * @property {ResponseBoard} data
 * @property {string} path
 * @property {CommonBoardRatio} ratio
 * @property {import('grapesjs').Component|import('grapesjs').Component[]} component
 * @property {import('grapesjs').CssRule[]} styleList
 * @property {string} initHtml
 * @property {string} initCss
 */
/**
 * @typedef {object} UserInfoReduce
 * @property {string} _id
 * @property {string} userName
 */
/**
 * @typedef {object} ResponsePage
 * @property {string} _id
 * @property {UserInfoReduce} writer
 * @property {UserInfoReduce} updater
 * @property {string} name
 * @property {ResponseConnectedBoard[]} connectedBoards
 * @property {Object[]} boardTree
 * @property {string[]} assets
 * @property {string} status
 * @property {string} productNo
 * @property {string} file
 * @property {string?} reserveId
 * @property {string?} reserveTime
 * @property {string?} expiresAt
 * @property {string} createdAt
 * @property {string} updatedAt
 */
/**
 * @typedef {object} ResponseConnectedBoard
 * @property {string} _id
 * @property {string} type
 * @property {string} name
 * @property {string[]} commonScripts
 * @property {string[]} commonStyles
 * @property {string} content
 * @property {string} style
 * @property {string[]} scripts
 * @property {string?} expiresAt
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {object} ResponseBoard
 * @property {string} _id
 * @property {string} type
 * @property {string} name
 * @property {UserInfoReduce?} writer
 * @property {UserInfoReduce?} updater
 * @property {string[]} commonScripts
 * @property {string[]} commonStyles
 * @property {string[]} scripts
 * @property {string} content
 * @property {string} style
 * @property {string?} file
 * @property {string?} url
 * @property {boolean} isSet
 * @property {string?} expiresAt
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {object} SaveBoardInfo
 * @property {string} path
 * @property {string} name
 * @property {string} type
 * @property {string[]} commonScripts
 * @property {string[]} commonStyles
 * @property {string[]} scripts
 * @property {string} content
 * @property {string} style
 */

/**
 * @typedef {object} CommentInfo
 * @property {string} _id
 * @property {string} page
 * @property {string} connectedBoard
 * @property {UserInfoReduce} user
 * @property {string} content
 * @property {number} depth
 * @property {CommentInfo[]} replies
 * @property {boolean} resolved
 * @property {string} createdAt
 * @property {string} updatedAt
 */
/**
 * @typedef {object} UndoStack
 * @property {string} action
 * @property {object?} before
 * @property {object} after
 * @property {string?} parentId
 */
export class AppEditorStore {
    store;
    /** @type {import('grapesjs').Editor} */
    editor;
    /** @type {import('grapesjs').Editor} */
    commonEditor;
    isSelectingTarget = false;
    transitionTime = 152;
    updateAppShell = true;
    editorKey = 0;
    analytics = true;
    queryParams = {};
    /** @type {import('grapesjs').Component[]} */
    componentList = [];
    /** @type {PageInfo} */
    pageInfo = {
        id: undefined,
        file: undefined,
        title: undefined,
        commonStyles: [],
        commonScripts: [],
        assets: [],
        scripts: [],
        status: undefined,
        productNo: undefined,
        parentPath: undefined,
        reserveId: undefined,
        reserveTime: undefined
    }
    /** @type {Record<string,ConnectedBoardInfo>} */
    connectedBoardInfoMap = {};
    /** @type {BoardCommentMap} */
    boardCommentMap = {
        selector: undefined, boardType: undefined,
    };
    /** @type {string} */
    layerHoverPreview = undefined
    /** @type {string[]} */
    selectedConnectedBoardIds = [];
    /** @type {CommonBoardEditorModal} */
    commonBoardEditorModal = {
        state: false,
        data: {},
        path: undefined,
        ratio: {},
        component: undefined,
        styleList: [],
        selectedLink: "",
        initHtml: "",
        initCss: ""
    };
    scriptQueue = [];
    commonBoardScriptQueue = [];
    /** @type {string} */
    headerCommentBoardId = undefined;
    commentWriteValue = '';

    initHtml = "";
    initCss = "";
    initBoardTree = [];
    selectedLink = "";
    pageStatusList = [
        {value: "WORKING", label: "작업중"},
        {value: "REQUEST_INSPECT", label: "검수 요청"},
        {value: "REQUEST_RE_MODIFICATION", label: "재수정 요청"},
        {value: "CHECK_COMPLETE", label: "확인 완료"},
        {value: "CONNECTED", label: "연결됨"}
    ];
    isReserved = false;
    boardTabChk = false;
    disEditable = false;
    editableUserName = null;
    queue;
    fingerprint = undefined;

    /** @type {any[]} */
    boardTree = [];
    isScreenshot = false;
    currentPageSessionUser = undefined;


    constructor(store, initialState) {
        this.store = store;
        this.queue = new Queue();
        initialState && Object.keys(initialState).forEach((key) => {
            const keyId = key;
            const value = initialState[keyId];
            if (value !== undefined) {
                // @ts-ignore Have to prevent readonly properties
                this[keyId] = value;
            }
        });

        this.queryParams = parseParamsToObject(window.location.search);
        makeAutoObservable(this, {}, {autoBind: true});
    }

    * init() {
        yield this.editorKey++;
    }

    /**
     * 공통보드 모달 데이터를 초기화하는 함수
     */
    clearCommonBoardEditorModal() {
        this.commonBoardEditorModal = {
            state: false,
            data: {},
            path: undefined,
            ratio: {},
            component: undefined,
            styleList: [],
            selectedLink: "",
            initHtml: "",
            initCss: ""
        };
        // if (this.commonEditor) {
        //     this.commonEditor = undefined;
        // }

    }

    /**
     * 페이지 정보를 초기화하기 위한 함수
     * 에디터에서 나갈때 호출
     */
    clearPage() {
        this.componentList = [];
        this.pageInfo = {
            id: undefined,
            file: undefined,
            title: undefined,
            commonStyles: [],
            commonScripts: [],
            assets: [],
            scripts: [],
            status: undefined,
            productNo: undefined,
            parentPath: undefined,
            reserveId: undefined,
            reserveTime: undefined
        }
        this.isReserved = false;
        this.connectedBoardInfoMap = {};
        this.boardCommentMap = {
            selector: undefined, boardType: undefined
        };
        this.layerHoverPreview = undefined;
        this.commonBoardEditorModal = {};
        this.commentWriteValue = '';
        if (this.editor && this.editor.DomComponents) {
            this.editor.DomComponents.clear();
        }
        this.editor.DomComponents.getComponents().models.map(component => component.destroy());
        this.editor.CssComposer.getRules().map(cssRule => cssRule.destroy());
        this.selectedLink = "";
        this.initHtml = '';
        this.initCss = "";
        this.initBoardTree = [];
        this.boardTabChk = false;
        this.boardTree = [];
        this.disEditable = false;
        this.editableUserName = null;
        this.isScreenshot = false;
        this.fingerprint = undefined;
        this.currentPageSessionUser = undefined;
    }

    /**
     * 기본적으로 에디터에 진입시 페이지 진입 시 에디터 세팅함수
     * @param {string} fileId 페이지데이터
     */
    async setPageByFile(fileId) {
        const response = await axios.get(`/page?fileId=${fileId}`);
        /** @type {ResponseSuccessForm} */
        const responseData = response.data;
        /** @type {ResponsePage} */
        const page = responseData.data.page;
        const parentPath = responseData.data.parentPath;
        await this.setPage(page, parentPath.split("PAGE,")[1].slice(0, -1));
        this.pageInfo.file = fileId;
        await this.checkPageSession(fileId);
    }


    /**
     * 자동저장 템플릿이 있을 경우 에디터 페이지 진입 시 에디터 세팅함수
     * @param {{page: object, parentPath: string}} autoData 자동저장된 페이지 데이터
     */
    async setPageByAutoSaveData({page, parentPath}) {
        await this.setPage(page, parentPath);
        this.pageInfo.file = page.file;
        await this.checkPageSession(page.file);
    }


    /**
     * 페이지 정보를 세팅하는 함수
     * @param {ResponsePage} page 페이지데이터
     * @param {string} parentPath 파일이 위치한 경로
     * @return {Promise<void>}
     */
    async setPage(page, parentPath) {
        // if (this.editor && this.editor.DomComponents.getComponents().models.length > 0) {
        //     this.setPageInfo(page, parentPath);
        //     return;
        // }
        getUndoManager().clear();
        this.clearPage();

        this.setPageInfo(page, parentPath);

        runInAction(() => {
            this.isReserved = !!(page.reserveId && page.reserveTime && new Date(page.reserveTime) > new Date());
            this.boardTree = page.boardTree;
            if (this.disEditable || this.isReserved) {
                const body = this.editor.getWrapper();
                body.set('locked', true);
            }
        })

        const editor = this.editor;
        /** @type {Array<string>[]} */
        const componentInfoList = [];
        for (let i = 0; i < page.connectedBoards.length; i++) {
            const connectedBoard = page.connectedBoards[i];
            const connectedBoardId = connectedBoard._id || connectedBoard.id;
            this.connectedBoardInfoMap[connectedBoardId] = {
                commonStyles: connectedBoard.commonStyles, commonScripts: connectedBoard.commonScripts,
                scripts: connectedBoard.scripts, commentCount: connectedBoard.commentCount, type: connectedBoard.type,
                name: connectedBoard.name, url: connectedBoard.url, commonBoardOrigin: connectedBoard.commonBoardOrigin,
                content: connectedBoard.content, style: connectedBoard.style
            };
            /** 클립보드와 공통보드를 나누어서 세팅. */
            if (connectedBoard.type === 'CLIP') {
                /** 클립보드의 경우 html, css 를 전부 에디터에 직접적으로 add 하는 방식을 사용. 한번에 에디터에 입력하기 위해 정보만 리턴 */
                for (const commonStyle of connectedBoard.commonStyles) {
                    if (!this.pageInfo.commonStyles.includes(commonStyle)) this.pageInfo.commonStyles.push(commonStyle);
                }
                for (const commonScript of connectedBoard.commonScripts) {
                    if (!this.pageInfo.commonScripts.includes(commonScript)) this.pageInfo.commonScripts.push(commonScript);
                }
                for (const script of connectedBoard.scripts) {
                    if (!this.pageInfo.scripts.includes(script)) this.pageInfo.scripts.push(script);
                }
                componentInfoList.push([connectedBoardId,
                    `<div id="${createId(5)}" data-id="${connectedBoardId}" data-board-type="${connectedBoard.type}" data-name="${connectedBoard.name}" class="connected_board_wrapper">${connectedBoard.content}</div>`,
                    connectedBoard.style
                ]);
            } else if (connectedBoard.type === 'CLIP_SET') {
                let contentString = '';
                for (let i = 0; i < connectedBoard.commonStyles.length; i++) {
                    const commonStyles = JSON.parse(connectedBoard.commonStyles[i]);
                    for (const commonStyle of commonStyles) {
                        if (!this.pageInfo.commonStyles.includes(commonStyle)) this.pageInfo.commonStyles.push(commonStyle);
                    }
                    const commonScripts = JSON.parse(connectedBoard.commonScripts[i]);
                    for (const commonScript of commonScripts) {
                        if (!this.pageInfo.commonScripts.includes(commonScript)) this.pageInfo.commonScripts.push(commonScript);
                    }
                    const scripts = JSON.parse(connectedBoard.scripts[i]);
                    for (const script of scripts) {
                        if (!this.pageInfo.scripts.includes(script)) this.pageInfo.scripts.push(script);
                    }
                }
                const contents = JSON.parse(connectedBoard.content);
                for (const content of contents) {
                    contentString += content;
                }
                componentInfoList.push([connectedBoardId,
                    `<div id="${createId(5)}" data-id="${connectedBoardId}" data-board-type="${connectedBoard.type}" data-name="${connectedBoard.name}" class="connected_board_wrapper">${contentString}</div>`,
                    connectedBoard.style
                ]);
            } else if (connectedBoard.type === 'COMMON') {
                /** 공통보드의 경우 iframe 으로 s3에 저장되어있는 html과 css를 로드하는 형태. 한번에 에디터에 입력하기 위해 정보만 리턴 */
                this.connectedBoardInfoMap[connectedBoardId].url = connectedBoard.url;
                this.connectedBoardInfoMap[connectedBoardId].commonBoardOrigin = connectedBoard.commonBoardOrigin;
                componentInfoList.push([connectedBoardId,
                    this.getCommonBoardContent(connectedBoardId, connectedBoard.commonBoardOrigin, connectedBoard.url, connectedBoard.name),
                    null
                ])
            } else {
                for (const script of connectedBoard.scripts) {
                    if (!this.pageInfo.scripts.includes(script)) this.pageInfo.scripts.push(script);
                }
                const tree = this.boardTree[i]
                let className = "iv-tab-toggle";
                if (tree.data.isSize) className += ' iv-tab-size-guide vreview-personal-widget-btn';
                if (tree.data.isColor) className += ' prd_info'
                if (tree.data.isFitGuide) className += ' fit_guide'
                componentInfoList.push([connectedBoardId,
                    `<section id="${createId(5)}" data-id="${connectedBoardId}" data-board-type="${connectedBoard.type}" data-name="${connectedBoard.name}" class="${className}">${connectedBoard.content}</section>`,
                    connectedBoard.style]);
            }
        }
        /** 페이지에 사용된 에셋(이미지들)을 에디터에 세팅. 저장을 위해서 pageInfo 에도 세팅 */
        for (const asset of page.assets) {
            if (!this.pageInfo.assets.includes(asset)) {
                this.pageInfo.assets.push(asset);
                editor.Assets.add(asset);
            }
        }
        this.sortCommonStyle();
        for (const [connectedBoardId, content, style] of componentInfoList) {
            /** 데이터베이스에 저장되어있는 ConnectedBoard의 content의 경우 이미 클래스명에 난수가 포함되어있어서 별도의 작업없이 추가 */
            const component = editor.DomComponents.addComponent(content);
            /** 스타일의 경우 데이터베이스에 저장될 때 클래스명에 난수를 빼고 저장(보드로 저장하는 기능 때문)하기 때문에 content의 난수를 읽어와 추가 */
            if (style) {
                if (this.connectedBoardInfoMap[connectedBoardId].type === 'CLIP_SET') {
                    const styles = JSON.parse(style);
                    const childComponents = component.components().models;
                    for (const childComponent of childComponents) {
                        this.addStyle(styles[childComponent.index()], childComponent);
                    }
                } else {
                    this.addStyle(style, component.getChildAt(0));
                }
            }
            if (this.connectedBoardInfoMap[connectedBoardId].type === 'COMMON') {
                /** 공통보드의 경우 iframe 이 ratio 정보가 없으면 height가 명확하게 나오지 않아 해당 css 파일을 공통보드 저장할때 저장하고
                 * s3에서 읽어온 뒤 적용 */
                this.addCommonBoardSize(this.editor, this.connectedBoardInfoMap[connectedBoardId].url);
                // const cssRes = await axios.get(cssUrl);
                // this.editor.addStyle(cssRes.data);
            }
            if (this.connectedBoardInfoMap[connectedBoardId].type === 'TOGGLE') {
                component.set("locked", true)
            }
        }

        this.componentList = editor.DomComponents.getComponents().models;
        /** 처음 페이지 진입시 pc 버전으로 세팅 */
        // editor.setDevice('Desktop');
        /** 알림센터를 통해 진입시 해당 보드로 바로 이동하는 기능이 존재하기에 headerCOmmentBoardId가 있고 parentComment가 존재할 때 이동 */
        if (this.headerCommentBoardId) {
            const parentComponent = this.componentList.find((component) => component.attributes.attributes['data-id'] === this.headerCommentBoardId);
            if (parentComponent) {
                const topPosition = Math.floor(parentComponent.view.el.offsetTop - 5);
                const frameWindow = this.editor.Canvas.getWindow();
                setTimeout(function () {
                    frameWindow.scrollTo({behavior: 'smooth', top: topPosition});
                }, 500);
            }
            this.headerCommentBoardId = undefined;
        }
        this.initBoardTree = JSON.parse(JSON.stringify(page.boardTree));
        this.refreshComponentList();
        this.editor.Commands.run('reload-scripts');
        if (!this.fingerprint) {
            this.fingerprint = await this.getFingerprint();
        }
    }

    setPageInfo(page, parentPath) {
        runInAction(() => {
            this.pageInfo.id = page._id;
            this.pageInfo.title = page.name;
            this.pageInfo.status = page.status;
            this.pageInfo.productNo = page.productNo;
            this.pageInfo.parentPath = parentPath;
            this.pageInfo.reserveId = page.reserveId;
            this.pageInfo.reserveTime = page.reserveTime;
        });
    }

    /**
     * 현재 에디터 사용 중인 유저 세팅 및 페이지 세션 세팅 함수
     * @param {string} fileId 에디터의 구분 할 고유 파일 아이디
     */
    async checkPageSession(fileId) {
        if (!fileId) {
            return;
        }
        const pageSessionRes = await this.getPageSession(fileId);
        try {
            if (!pageSessionRes.pageSession) { // 아무도 사용중이지 않는 경우 진입시 현재 세션 사용자를 본인으로 지정
                await axios.post("/page/session", {pageId: fileId});
                getSocketStore().startPageSession(fileId);
                this.currentPageSessionUser = getUserStore().me;
            } else { // 사용자가 있는 경우
                if (pageSessionRes.pageSession.user) {
                    this.currentPageSessionUser = pageSessionRes.pageSession.user; // 사용자를 세션 사용자에 지정
                    if (pageSessionRes.pageSession.user._id === getUserStore().me._id) { // 사용자가 본인일 경우
                        getSocketStore().startPageSession(fileId);
                    } else { // 사용자가 본인이 아닐경우
                        this.disEditable = true;
                        this.editableUserName = pageSessionRes.pageSession.user.userName;
                    }
                }
            }
        } catch (e) {
            console.error("세션 생성 실패", e);
        }
    }


    /**
     * 에디터 작업시 현재 기준 에디터 템플릿 저장 함수
     */
    async setTempSave() {
        try {
            const connectedBoardInfoList = this.editor.DomComponents && await this.getSaveConnectedBoardInfoList(this.editor.DomComponents.getComponents());
            if (connectedBoardInfoList && connectedBoardInfoList.length > 0) {
                const saveDataInfo = {
                    page: {
                        _id: this.pageInfo.id, file: this.pageInfo.file,
                        connectedBoards: connectedBoardInfoList,
                        boardTree: this.boardTree,
                        name: this.pageInfo.title, status: this.pageInfo.status, assets: this.pageInfo.assets,
                        productNo: this.pageInfo.productNo, parentPath: this.pageInfo.parentPath,
                        reserveId: this.pageInfo.reserveId, reserveTime: this.pageInfo.reserveTime
                    }, parentPath: this.pageInfo.parentPath
                }
                await axios.post("/page/tempSave", {
                    fingerprint: this.fingerprint,
                    data: JSON.stringify(saveDataInfo),
                });
            }
        } catch (e) {
            console.log(e)
        }

    }

    /**
     * 자동 저장 됐던 템플릿 가져오는 함수
     * @return {Promise<void>}
     */
    async getTempSave() {
        try {
            const response = await axios.get(`/page/tempSave?fingerprint=${this.fingerprint}`);
            return response.data.data;
        } catch (e) {
            console.log(e)
        }
    }

    /**
     * 자동 저장 됐던 템플릿 삭제 함수
     */
    async deleteTempSave() {
        await axios.delete("/page/tempSave", {data: {fingerprint: this.fingerprint}});
    }

    /**
     * 현재 에디터 페이지 자용 중인 정보를 가져오는 함수
     * @param {string} id 에디터의 구분 할 고유 파일 아이디
     */
    async getPageSession(id) {
        try {
            const response = await axios.get(`/page/session?pageId=${id}`);
            return response.data.data;
        } catch (e) {
            console.log(e)
        }
    }

    /**
     * 웹사이트 방문자의 브라우저, 기기, 환경 정보를 수집하여
     * 고유한 식별자(fingerprint)를 생성 함수
     * @return {Promise<string>}
     */
    async getFingerprint() {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        return result.visitorId;
    }

    /**
     * 공통보드 에디터를 세팅하는 함수
     * @param {import('grapesjs').Editor} editor
     */
    setCommonEditor(editor) {
        this.commonEditor = editor;
    }

    /**
     * 공통 스크립트의 정렬을 위한 함수
     * 공통 스크립트들도 순서가 지켜져야 하는 경우가 있어서 사용됨
     * 외부스크립트, 내부공용스크립트, 내부스크립트 순으로 정렬
     */
    sortCommonStyle() {
        const publicStyle = [];
        const ourStyle = [];
        const ourCommonStyle = [];
        for (const style of this.pageInfo.commonStyles) {
            if (style.indexOf(import.meta.env.CLOUDFRONT_URK) > 0) {
                if (style.indexOf("andar-common-01.css") > 0) {
                    ourCommonStyle.push(style)
                } else {
                    ourStyle.push(style);
                }
            } else {
                publicStyle.push(style);
            }
        }
        this.pageInfo.commonStyles = [...publicStyle, ...ourCommonStyle, ...ourStyle]
    }

    /**
     * 공통보드의 컨텐츠를 담은 iframe html을 리턴하는 함수
     * @param {string} connectedBoardId 연결보드의 고유 아이디
     * @param {string} originBoardId 공통보드의 고유 아이디
     * @param {string} url 공통보드가 저장된 url
     * @param {string} name 공통보드의 이름
     * @return {string}
     */
    getCommonBoardContent(connectedBoardId, originBoardId, url, name) {
        url = url.replace(/\+/g, '%2B');
        const commonBoardRandomId = `common-${originBoardId}`;
        const wrapperId = createId(10);
        let result = `<div data-id="${connectedBoardId}" data-board-type="COMMON" data-name="${name}" class="connected_board_wrapper prev-detail-component" id="${wrapperId}">`;
        result += `<iframe class="${commonBoardRandomId}" src="${url}?time=${new Date().getTime()}" allowfullscreen></iframe></div>`;
        return result;
    }

    /**
     * 선택된 컴포넌트의 최상위 컴포넌트를 찾아 에디터의 위치를 이동시켜주는 함수
     * @param {string} selector 연결보드 셀렉터 (토글 타입 제외)
     */
    moveToComponent(selector) {
        let targetComponent;
        for (const component of this.componentList) {
            const connectedBoardInfo = this.connectedBoardInfoMap[component.attributes.attributes['data-id']]
            if (connectedBoardInfo.type === 'CLIP_SET') {
                const selectors = component.components().models.map(comp => this.getComponentSelector(comp).selector);
                if (selectors.includes(selector)) targetComponent = component;
            } else {
                const componentSelector = this.getComponentSelector(component.getChildAt(0)).selector;
                if (selector === componentSelector) {
                    targetComponent = component;
                }
            }
        }
        if (targetComponent) {
            const topPosition = Math.floor(targetComponent.view.el.offsetTop - 5);
            const frameWindow = this.editor.Canvas.getWindow();
            frameWindow.scrollTo({behavior: 'smooth', top: topPosition});
        }
    }

    /**
     * 선택된 컴포넌트의 최상위 컴포넌트를 찾아 에디터의 위치를 이동시켜주는 함수
     * @param {number} index 연결보드 셀렉터 인덱스 값 (토글 타입 만)
     */
    moveToToggleComponent(index) {
        const targetComponent = this.componentList[index];
        if (targetComponent) {
            const topPosition = Math.floor(targetComponent.view.el.offsetTop - 5);
            const frameWindow = this.editor.Canvas.getWindow();
            frameWindow.scrollTo({behavior: 'smooth', top: topPosition});
        }
    }

    /**
     * 연결보드를 저장할 때, 연결보드의 정보 리스트를 편집하여 리턴하는 함수
     * Component, CssRule 등의 객체를 스트링으로 변환하여 리턴
     * @param componentList
     * @param {Boolean?} withoutRandomClasses 랜덤클래스 허용 여부
     * @return {Promise<ConnectedBoardInfo[]>}
     */
    async getSaveConnectedBoardInfoList(componentList, withoutRandomClasses = false) {
        /** @type {ConnectedBoardInfo[]} */
        const connectedBoardInfoList = [];
        for (const component of componentList) {
            const connectedBoardInfo = this.connectedBoardInfoMap[component.attributes.attributes['data-id']];
            if (connectedBoardInfo.type === 'COMMON') {
                connectedBoardInfoList.push(Object.assign(connectedBoardInfo, {id: component.attributes.attributes['data-id']}));
            } else {
                const {
                    content,
                    style
                } = await this.getBoardContentsAndStyles(component, connectedBoardInfo.type, withoutRandomClasses)
                connectedBoardInfoList.push({
                    id: component.attributes.attributes['data-id'],
                    type: connectedBoardInfo.type,
                    name: connectedBoardInfo.name,
                    commonScripts: connectedBoardInfo.commonScripts,
                    commonStyles: connectedBoardInfo.commonStyles,
                    content: content,
                    style: style,
                    scripts: connectedBoardInfo.scripts
                });
            }

        }
        return connectedBoardInfoList;
    }

    /**
     * 페이지를 저장할 때 사용하는 함수
     * @param {string} path 저장할 경로
     * @param {boolean} force 덮어쓰기 여부
     * @return {Promise<ResponsePage>|void}
     */
    async savePage(path, force) {
        const targetPath = path.indexOf(",PAGE") > -1 ? path.split(`PAGE,`)[1].slice(0, -1) : path;
        if (this.componentList.length === 0) {
            return;
        }
        const connectedBoardInfoList = await this.getSaveConnectedBoardInfoList(this.editor.DomComponents.getComponents());
        if (this.pageInfo.status === null) {
            this.pageInfo.status = "WORKING";
        }

        const saveData = {
            path: targetPath,
            name: this.pageInfo.title,
            connectedBoardInfos: connectedBoardInfoList,
            boardTree: this.boardTree,
            assets: this.pageInfo.assets, status: this.pageInfo.status, force: force
        }
        const response = await axios.post(`/page/save`, saveData);
        /** @type {ResponsePage} */
        const page = response.data.data.page;
        await this.setPage(page, path);
        getUndoManager().clear();
        return page;
    }

    /**
     * 연결된 페이지를 라이브에 적용하기 위한 함수
     * @return {Promise<ResponseSuccessForm|void>}
     * @param {string?} date 라이브 예약 시간
     */
    async livePage(date = undefined) {
        if (this.componentList.length === 0) {
            return;
        }
        const connectedBoardInfoList = await this.getSaveConnectedBoardInfoList(this.editor.DomComponents.getComponents());
        const liveData = {
            pageId: this.pageInfo.id,
            name: this.pageInfo.title,
            connectedBoardInfos: connectedBoardInfoList,
            boardTree: this.boardTree,
            assets: this.pageInfo.assets,
            status: this.pageInfo.status
        }
        if (date) liveData.reserveTime = date;
        const response = await axios.post(`/page/live`, liveData);
        const page = response.data.data.page;
        await this.setPageByFile(page.file);
        // getUndoManager().clear();
    }

    /**
     * 라이브 예약을 해제시키는 함수
     */
    async liveCancelPage() {
        const response = await axios.post("/page/cancelReserve", {
            pageId: this.pageInfo.id,
            jobId: this.pageInfo.reserveId,
        });
        const page = response.data.data.page;
        await this.setPageByFile(page.file);
    }

    /**
     * 연결보드를 템플릿으로 저장할 때 사용되는 함수
     * @param {string} path 저장할 경로
     * @param {string} title 저장할 보드의 이름
     * @param {import('grapesjs').Component} saveItem 컴포넌트 리스트
     * @param {boolean} force 덮어쓰기 여부
     * @return {Promise<ResponseSuccessForm>}
     */
    async saveToTemplateBoard(path, title, saveItem, force) {
        const boardInfo = await this.getSaveBoardInfo(path, title, saveItem);
        boardInfo['type'] = "TEMPLATE";
        if (force) {
            boardInfo['force'] = force;
        }
        const response = await axios.post(`/board/save`, boardInfo);
        return response.data;
    }


    /**
     * 연결보드를 클립보드로 저장할 때 사용되는 함수
     * @param {string} path 저장할 경로
     * @param {string} title 저장할 보드의 이름
     * @param {import('grapesjs').Component} saveItem 컴포넌트 리스트
     * @param {boolean} force 덮어쓰기 여부
     * @return {Promise<ResponseSuccessForm>}
     */
    async saveToClipBoard(path, title, saveItem, force) {
        const boardInfo = await this.getSaveBoardInfo(path, title, saveItem);
        if (force) {
            boardInfo['force'] = force;
        }
        const response = await axios.post(`/board/save`, boardInfo);

        return response.data;
    }

    /**
     * 공통보드를 저장하는 함수
     * @param {string} path 저장할 경로
     * @param {string} title 공통보드 이름
     * @param {import('grapesjs').Component} saveItem 컴포넌트 리스트
     * @param {boolean} force 덮어쓰기 여부
     * @return {Promise<ResponseSuccessForm>}
     */
    async saveToCommonBoard(path, title, saveItem, force) {
        const commonBoardRatio = await this.getCommonBoardRatio(saveItem.attributes.attributes['id']);
        const {commonScripts, commonStyles, scripts} = this.commonBoardEditorModal.data;
        const {
            content,
            style
        } = await this.getBoardContentsAndStyles(this.commonBoardEditorModal.component, this.commonBoardEditorModal.data.isSet || this.commonBoardEditorModal.data.type === 'CLIP_SET' ? 'CLIP_SET' : 'COMMON', true);
        const boardInfo = {
            type: 'COMMON',
            name: title,
            path,
            isSet: this.commonBoardEditorModal.data.isSet || this.commonBoardEditorModal.data.type === 'CLIP_SET',
            commonScripts,
            commonStyles,
            content,
            style,
            scripts,
            commonBoardRatio: commonBoardRatio,
        }

        if (force) {
            boardInfo['force'] = force;
        }
        const response = await axios.post(`/board/save`, boardInfo);
        return response.data;
    }

    /**
     * 공통보드 업데이트에 사용되는 함수
     * @return {Promise<ResponseSuccessForm|void>}
     */
    async updateCommonBoard() {
        if (this.commonBoardEditorModal.state === false || Object.keys(this.commonBoardEditorModal.data).length === 0) {
            return;
        }
        /** @type {CommonBoardRatio} */
        const commonBoardRatio = await this.getCommonBoardRatio(this.commonBoardEditorModal.component.attributes.attributes['id']);
        let style, content;
        /** @type {object[]} */
            // const styleList = [];
            // for (const cssRule of this.commonBoardEditorModal.styleList) {
            //     styleList.push(cssRule.toJSON())
            // }

        const {commonScripts, commonStyles, scripts, name} = this.commonBoardEditorModal.data;
        const updateContentAndStyles = await this.getBoardContentsAndStyles(this.commonBoardEditorModal.component, this.commonBoardEditorModal.data.isSet ? 'CLIP_SET' : 'COMMON', true);
        content = updateContentAndStyles.content;
        style = updateContentAndStyles.style;
        const boardInfo = {
            type: 'COMMON',
            name,
            path: this.commonBoardEditorModal.path.split(`COMMON,`)[1].slice(0, -1),
            isSet: this.commonBoardEditorModal.data.isSet,
            commonScripts,
            commonStyles,
            content,
            style,
            scripts,
            commonBoardRatio: commonBoardRatio,
            force: true
        }
        const response = await axios.post(`/board/save`, boardInfo);
        this.commonBoardEditorModal.initHtml = this.getContentHtml([this.commonBoardEditorModal.component]);
        this.commonBoardEditorModal.initCss = this.getContentCss([this.commonBoardEditorModal.component]);
        this.commonBoardEditorModal.data.content = content;
        this.commonBoardEditorModal.data.style = style;
        return response.data;
    }

    /**
     * 공통보드로 저장하는 기능을 실행할 때 공통보드의 가로 세로 비율을 구하는 함수
     * 데스크탑 비율과 모바일 비율을 모두 구하여 리턴
     * @param {string} elementId 엘리먼트의 아이디
     * @return {Promise<CommonBoardRatio>}
     */
    async getCommonBoardRatio(elementId) {
        const element = this.commonEditor.Canvas.getDocument().getElementById(elementId);
        // console.log(component.view.el.offsetHeight);
        const rect = element.getBoundingClientRect();
        let deviceList = ['Desktop', 'Accordion', 'Mobile'];

        const component = this.commonBoardEditorModal.component;
        let elPaddingLeft, elPaddingRight, elRectMarginTop, elRectMarginBottom;
        elPaddingLeft = window.getComputedStyle(element).paddingLeft ? parseInt(window.getComputedStyle(element).paddingLeft.match(/\d+/)[0], 10) : 0;
        elPaddingRight = window.getComputedStyle(element).paddingRight ? parseInt(window.getComputedStyle(element).paddingRight.match(/\d+/)[0], 10) : 0;
        elRectMarginTop = this.getComponentMargin(component, 'Top') ? parseInt(this.getComponentMargin(component, 'Top').replace('px', '')) : 0;
        elRectMarginBottom = this.getComponentMargin(component, 'Bottom') ? parseInt(this.getComponentMargin(component, 'Bottom').replace('px', '')) : 0;
        let device = this.commonEditor.getDevice();
        const commonBoardRatio = {};
        commonBoardRatio[device] = {
            width: rect.width - (elPaddingLeft + elPaddingRight),
            height: rect.height + (elRectMarginTop + elRectMarginBottom)
        };
        deviceList = deviceList.filter(dev => dev !== device);
        let nextDevice = deviceList.pop();
        let targetWidth = nextDevice === 'Desktop' ? 775 : nextDevice === 'Accordion' ? 711 : 400;

        this.commonEditor.setDevice(nextDevice);
        while (true) {
            const newElement = this.commonEditor.Canvas.getDocument().getElementById(elementId);
            const newRect = newElement.getBoundingClientRect();
            elPaddingLeft = window.getComputedStyle(newElement).paddingLeft ? parseInt(window.getComputedStyle(newElement).paddingLeft.match(/\d+/)[0], 10) : 0;
            elPaddingRight = window.getComputedStyle(newElement).paddingRight ? parseInt(window.getComputedStyle(newElement).paddingRight.match(/\d+/)[0], 10) : 0;
            elRectMarginTop = this.getComponentMargin(component, 'Top') ? parseInt(this.getComponentMargin(component, 'Top').replace('px', '')) : 0;
            elRectMarginBottom = this.getComponentMargin(component, 'Bottom') ? parseInt(this.getComponentMargin(component, 'Bottom').replace('px', '')) : 0;
            if (parseInt(`${newRect.width}`) === targetWidth) {
                commonBoardRatio[nextDevice] = {
                    width: newRect.width - (elPaddingLeft + elPaddingRight),
                    height: newRect.height + (elRectMarginTop + elRectMarginBottom)
                }
                break;
            }
            await timeout(50);
        }
        nextDevice = deviceList.pop();
        targetWidth = nextDevice === 'Desktop' ? 775 : nextDevice === 'Accordion' ? 711 : 400;

        this.commonEditor.setDevice(nextDevice);
        while (true) {
            const newElement = this.commonEditor.Canvas.getDocument().getElementById(elementId);
            const newRect = newElement.getBoundingClientRect();
            elPaddingLeft = window.getComputedStyle(newElement).paddingLeft ? parseInt(window.getComputedStyle(newElement).paddingLeft.match(/\d+/)[0], 10) : 0;
            elPaddingRight = window.getComputedStyle(newElement).paddingRight ? parseInt(window.getComputedStyle(newElement).paddingRight.match(/\d+/)[0], 10) : 0;
            elRectMarginTop = this.getComponentMargin(component, 'Top') ? parseInt(this.getComponentMargin(component, 'Top').replace('px', '')) : 0;
            elRectMarginBottom = this.getComponentMargin(component, 'Bottom') ? parseInt(this.getComponentMargin(component, 'Bottom').replace('px', '')) : 0;
            if (parseInt(`${newRect.width}`) === targetWidth) {
                commonBoardRatio[nextDevice] = {
                    width: newRect.width - (elPaddingLeft + elPaddingRight),
                    height: newRect.height + (elRectMarginTop + elRectMarginBottom)
                }
                break;
            }
            await timeout(50);
        }
        this.commonEditor.setDevice(device);
        return commonBoardRatio;
    }

    getComponentMargin(component, direction, margin = '0px') {
        let componentMargin;
        try {
            componentMargin = window.getComputedStyle(component.view.el)[`margin${direction}`];
        } catch (e) {
            return margin;
        }


        if (componentMargin !== '0px') {
            return componentMargin;
        }
        const childComponents = component.components().models;
        if (!childComponents || childComponents.length === 0) {
            return margin;
        }
        return this.getComponentMargin(direction === 'Top' ? childComponents[0] : childComponents[childComponents.length - 1], direction, margin);
    }


    /**
     * 컴포넌트로부터 클립보드 또는 공통보드로 저장될때 필요한 정보들을 가져오는 함수
     * content, style 의 클래스명에 포함된 랜덤 난수를 제거하고 스트링으로 변환하여 리턴
     * @param {string} path 저장할 경로
     * @param {string} title 보드의 이름
     * @param {import('grapesjs').Component} component 연결보드의 고유 아이디
     * @param {string} commonType common 타입만 지정
     * @return {Promise<SaveBoardInfo>}
     */
    async getSaveBoardInfo(path, title, component, commonType = undefined) {
        const {
            commonScripts,
            commonStyles,
            scripts,
            name,
            content,
            style,
            isSet
        } = await this.getBoardInfoFromComponent(component, true);
        return {
            path,
            name: title ? title : name,
            type: !commonType ? this.connectedBoardInfoMap[component.attributes.attributes['data-id']].type : commonType,
            commonScripts,
            commonStyles,
            scripts,
            content,
            style,
            isSet
        };
    }

    /**
     * CLIP_SET 병렬 배열(scripts/commonScripts/commonStyles) 길이를 콘텐츠 개수에 맞춤
     * 그룹 유지한 채 자식만 삭제하면 content/style만 줄고 scripts*는 남는 불일치를 방지
     * @param {string[]|undefined} scripts
     * @param {string[]|undefined} commonScripts
     * @param {string[]|undefined} commonStyles
     * @param {number} targetLength
     * @return {{scripts: string[], commonScripts: string[], commonStyles: string[]}}
     */
    syncClipSetMetaArrays(scripts, commonScripts, commonStyles, targetLength) {
        const sync = (arr) => {
            const list = Array.isArray(arr) ? arr.slice(0, targetLength) : [];
            while (list.length < targetLength) list.push('[]');
            return list;
        };
        return {
            scripts: sync(scripts),
            commonScripts: sync(commonScripts),
            commonStyles: sync(commonStyles),
        };
    }

    async getBoardInfoFromComponent(component, withoutRandomClasses = false) {
        const connectedBoardId = component.attributes.attributes['data-id'];
        /** @type {string} */
        const {
            content,
            style
        } = await this.getBoardContentsAndStyles(component, component.attributes.attributes['data-board-type'], withoutRandomClasses)

        const {commonScripts, commonStyles, scripts, name, type} = this.connectedBoardInfoMap[connectedBoardId];
        const isSet = type === 'CLIP_SET';
        if (isSet) {
            const contentLength = JSON.parse(content).length;
            const synced = this.syncClipSetMetaArrays(scripts, commonScripts, commonStyles, contentLength);
            Object.assign(this.connectedBoardInfoMap[connectedBoardId], synced);
            return {connectedBoardId, ...synced, name, content, style, isSet};
        }
        return {connectedBoardId, commonScripts, commonStyles, scripts, name, content, style, isSet};
    }

    /**
     * 에디터에 새로운 보드를 추가할 때 사용되는 함수
     * 인덱스가 있는 경우 해당 인덱스에 추가하고 없는 경우 맨 아래에 추가
     * @param {string} fileId 보드 파일의 고유 아이디
     * @return {Promise<import('grapesjs').Component|import('grapesjs').Component[]>}
     */
    async addBoard(fileId) {
        const response = await axios.get(`/board?fileId=${fileId}`);
        /** @type {ResponseSuccessForm} */
        const result = response.data;
        /** @type {ResponseBoard} */
        const board = result.data['board'];
        let checkedBoardIndex = undefined;
        const checkedBoards = this.boardTree.filter(tree => tree.parent !== 0 && tree.data.checked);
        if (checkedBoards.length === 1) {
            checkedBoardIndex = this.boardTree.findIndex(tree => tree.id === checkedBoards[0].id) + 1;
        } else {
            const isFitGuide = this.boardTree.findIndex((tree) => tree.data.isFitGuide);
            if (isFitGuide !== -1) {
                checkedBoardIndex = isFitGuide;
            }
        }
        const tree = {
            id: this.boardTree.length > 0 ? Math.max(...this.boardTree.map(tree => tree.id)) + 1 : 1,
            parent: (checkedBoards.length === 1) ? checkedBoards[0].parent : 0,
            text: board.name,
            droppable: false,
            data: {type: board.type}
        }

        const component = await this.processAddBoard(board, tree, checkedBoardIndex);
        if (Array.isArray(component)) {
            const connectedBoardIdList = component.map(comp => comp.attributes.attributes['data-id']);
            getUndoManager().add(new AddMultiStack(null, {connectedBoardIdList}))
        } else {
            getUndoManager().add(new AddStack(null, {connectedBoardId: component.attributes.attributes['data-id']}));
        }
        return component;
    }


    async cloneBoard(connectedBoardId) {
        const component = this.componentList.find(comp => comp.attributes.attributes['data-id'] === connectedBoardId);
        const boardInfo = await this.getBoardInfoFromComponent(component);
        boardInfo.type = this.connectedBoardInfoMap[connectedBoardId].type;
        delete boardInfo['connectedBoardId'];
        const tree = this.boardTree.find(node => node.data['data-id'] === connectedBoardId);
        const newTree = {
            id: this.boardTree.length > 0 ? Math.max(...this.boardTree.map(tree => tree.id)) + 1 : 1,
            parent: tree.parent,
            text: tree.text,
            droppable: false,
            data: {type: tree.data.type}
        }
        const clonedComponent = await this.processAddBoard(boardInfo, newTree, component.index() + 1, uuid());
        getUndoManager().add(new AddStack(null, {connectedBoardId: clonedComponent.attributes.attributes['data-id']}));
        return clonedComponent;
    }

    makeNewTreeInfo(name, type) {
        // let groupNum = 1;
        let toggleName = name;
        const dataId = uuid();
        let isColor = false;
        let isSize = false;
        let isFitGuide = false;
        if (type === 'unique') {
            isColor = true;
        } else if (type === 'guide') {
            isSize = true;
        } else if (type === 'fit_guide') {
            isFitGuide = true;
        }
        const tree = {
            id: this.boardTree.length > 0 ? Math.max(...this.boardTree.map(tree => tree.id)) + 1 : 1,
            text: toggleName,
            parent: 0,
            droppable: !isSize,
            data: {
                type: 'TOGGLE',
                'data-id': dataId
            },
            showAccordion: false,
        }
        if (isSize) {
            tree.data.isSize = isSize;
        }
        if (isColor) {
            tree.data.isColor = isColor;
        }
        if (isFitGuide) {
            tree.data.isFitGuide = isFitGuide;
        }
        return tree;
    }

    processAddToggle(tree, name, type) {
        if (!tree) {
            tree = this.makeNewTreeInfo(name, type);
        }

        const content = `<h1>${name}</h1>`;
        let className = "iv-tab-toggle toggle-header-editor";
        if (type === "guide") className += ' iv-tab-size-guide vreview-personal-widget-btn';
        if (type === "unique") className += ' prd_info'
        const contentWrap = `<section class="${className}" data-id="${tree.data['data-id']}">${content}</section>`;
        const wrapComponent = this.editor.DomComponents.addComponent(contentWrap);
        this.connectedBoardInfoMap[tree.data['data-id']] = {
            commonStyles: [],
            commonScripts: [],
            scripts: [`${import.meta.env.VITE_CLOUDFRONT_URL}/scripts/initToggleTab.js`],
            commentCount: 0,
            type: 'TOGGLE',
            name: name,
            content,
            style: ''
        };
        if (!this.pageInfo.commonStyles.includes(`${import.meta.env.VITE_CLOUDFRONT_URL}/assets/styles/andar-common-01.css`)) {
            this.pageInfo.commonStyles.push(`${import.meta.env.VITE_CLOUDFRONT_URL}/assets/styles/andar-common-01.css`);
        }
        if (!this.pageInfo.scripts.includes(`${import.meta.env.VITE_CLOUDFRONT_URL}/scripts/initToggleTab.js`)) {
            this.pageInfo.scripts.push(`${import.meta.env.VITE_CLOUDFRONT_URL}/scripts/initToggleTab.js`);
        }
        wrapComponent.set("locked", true)
        return {component: wrapComponent, tree}
    }

    addToggle({tree = undefined, name = undefined, type = undefined}) {
        const addResult = this.processAddToggle(tree, name, type);
        tree = addResult.tree;
        runInAction(() => {
            // this.boardTree.push(tree);
            const isFitGuide = this.boardTree.findIndex((tree) => tree.data.isFitGuide);
            if (isFitGuide !== -1) {
                this.boardTree.splice(isFitGuide - 1, 0, tree);
            } else {
                this.boardTree = [...this.boardTree, tree];
            }
            this.componentList = this.editor.DomComponents.getComponents().models;
            this.refreshComponentList();
            this.editor.Commands.run("reload-scripts");
        })
        getUndoManager().add(new AddStack(null, {connectedBoardId: addResult.component.attributes.attributes['data-id']}));
        return addResult.component;
    }


    /**
     * 보드 정보를 바탕으로 에디터에 컴포넌트를 추가해주는 함수
     * @param {ResponseBoard} board 보드 정보
     * @param {object?} tree 보드 트리 정보
     * @param {number?} index 보드가 들어갈 인덱스 넘버
     * @param {string?} customId 저장되지 않은 연결보드의 아이디
     * @return {Promise<import('grapesjs').Component>}
     */
    async processAddBoard(board, tree, index, customId) {
        let component;
        if (board.type === 'COMMON') {
            component = await this.addCommonBoard(board, customId);
        } else if (board.type === 'CLIP') {
            component = await this.addClipBoard(board, customId);
        } else if (board.type === 'CLIP_SET') {
            component = await this.addClipBoardSet(board, customId);
        } else if (board.type === 'TEMPLATE') {
            if (board.isSet) {
                board.type = 'CLIP_SET';
                component = await this.addClipBoardSet(board, customId);
            } else {
                board.type = 'CLIP';
                component = await this.addClipBoard(board, customId);
            }

        } else {
            const addResult = this.processAddToggle(tree, tree.text);
            component = addResult.component;
        }
        tree.data['type'] = board.type;
        if (!tree.data['data-id']) {
            tree.data['data-id'] = component.attributes.attributes['data-id'];
        }

        if (index !== undefined) {
            const beforeBoardTree = this.boardTree.slice(0, index);
            const afterBoardTree = this.boardTree.slice(index, this.boardTree.length);
            this.boardTree = [...beforeBoardTree, tree, ...afterBoardTree];
        } else {
            this.boardTree = [...this.boardTree, tree];
        }
        this.componentList = this.editor.DomComponents.getComponents().models;
        this.refreshComponentList();
        this.editor.Commands.run("reload-scripts");
        return component;
    }

    async groupingClipBoards(components) {
        const boardInfoList = await this.getSaveConnectedBoardInfoList(components);
        const commonScripts = [];
        const commonStyles = [];
        const scripts = [];
        const styles = [];
        let contents = [];
        for (const boardInfo of boardInfoList) {
            commonScripts.push(JSON.stringify(boardInfo.commonScripts));
            commonStyles.push(JSON.stringify(boardInfo.commonStyles));
            scripts.push(JSON.stringify(boardInfo.scripts));
            contents.push(boardInfo.content);
            styles.push(boardInfo.style);
        }
        const content = JSON.stringify(contents);
        const style = JSON.stringify(styles);
        const firstIndex = components[0].index();
        let parentTreeIndex = this.boardTree[firstIndex].parent;
        const deleteConnectedBoardIds = components.map(component => component.attributes.attributes['data-id']);
        const deleteTrees = this.boardTree.filter((tree) => {
            if (deleteConnectedBoardIds.includes(tree.data['data-id'])) {
                delete tree.data.checked;
                return true;
            }
            return false;
        });
        const deleteInfos = await this.deletePublicInfoFromBoard(deleteConnectedBoardIds);
        this.boardTree = this.boardTree.filter(tree => !deleteConnectedBoardIds.includes(tree.data['data-id']));
        components.forEach((component) => {
            const cssRules = this.getOneDepthComponentCssRules(component.getChildAt(0));
            cssRules.forEach(cssRule => cssRule.destroy());
            component.destroy();
        });

        const newBoardInfo = this.makeTempBoardInfo({
            commonScripts,
            commonStyles,
            scripts,
            content,
            style,
            name: 'ClipSet',
            type: 'CLIP_SET'
        })
        const newConnectedBoardId = uuid();
        const newComponent = await this.processAddBoard(newBoardInfo, {
            id: this.boardTree.length > 0 ? Math.max(...this.boardTree.map(tree => tree.id)) + 1 : 1,
            parent: parentTreeIndex,
            text: 'ClipSet',
            droppable: false,
            data: {type: 'CLIP_SET'}
        }, firstIndex, newConnectedBoardId);
        this.refreshComponentList();
        this.editor.Commands.run('reload-scripts');
        getUndoManager().add(new GroupingClipboardsStack({
            deleteInfos,
            deleteTrees
        }, {connectedBoardId: newComponent.attributes.attributes['data-id']}));
    }

    async ungroupClipboardSet(component) {
        let originIndex = component.index();
        const componentInfo = await this.getBoardInfoFromComponent(component);
        let parentTreeIndex = this.boardTree[originIndex].parent;
        const deleteConnectedBoardIds = [component.attributes.attributes['data-id']];
        const deleteTrees = this.boardTree.filter((tree) => {
            if (deleteConnectedBoardIds.includes(tree.data['data-id'])) {
                delete tree.data.checked;
                return true;
            }
            return false;
        });
        const deleteInfos = await this.deletePublicInfoFromBoard(deleteConnectedBoardIds);
        this.boardTree = this.boardTree.filter(tree => !deleteConnectedBoardIds.includes(tree.data['data-id']));
        // CLIP_SET 스타일은 wrapper가 아니라 자식(1depth)에 연결되므로 자식별로 정리 후 destroy
        // (미정리 시 processAddBoard의 addStyle과 중복되어 동일 셀렉터 규칙이 쌓임)
        component.components().models.forEach((childComponent) => {
            const cssRules = this.getOneDepthComponentCssRules(childComponent);
            cssRules.forEach(cssRule => cssRule.destroy());
        });
        component.destroy();
        const newConnectedBoardIds = [];
        const componentInfoContent = JSON.parse(componentInfo.content);
        const componentInfoStyle = JSON.parse(componentInfo.style);
        // for (let i = componentInfo.commonScripts.length - 1; i >= 0; i--) {
        for (let i = 0; i < componentInfo.commonScripts.length; i++) {
            const newBoardInfo = this.makeTempBoardInfo({
                commonScripts: JSON.parse(componentInfo.commonScripts[i]),
                commonStyles: JSON.parse(componentInfo.commonStyles[i]),
                scripts: JSON.parse(componentInfo.scripts[i]),
                content: componentInfoContent[i],
                style: componentInfoStyle[i],
                name: `${componentInfo.name}-${i + 1}`,
                type: 'CLIP'
            })
            const newConnectedBoardId = uuid();
            await this.processAddBoard(newBoardInfo, {
                id: this.boardTree.length > 0 ? Math.max(...this.boardTree.map(tree => tree.id)) + 1 : 1,
                parent: parentTreeIndex,
                text: newBoardInfo.name,
                droppable: false,
                data: {type: newBoardInfo.type}
            }, originIndex++, newConnectedBoardId);
            newConnectedBoardIds.push(newConnectedBoardId);
        }
        this.refreshComponentList();
        this.editor.Commands.run('reload-scripts');
        getUndoManager().add(new UngroupClipboardsStack({connectedBoardIdList: newConnectedBoardIds}, {
            connectedBoardInfo: deleteInfos[0].connectedBoardInfo,
            componentIndex: deleteInfos[0].componentIndex,
            boardTree: deleteTrees[0]
        }));
    }

    makeTempBoardInfo({commonScripts, commonStyles, scripts, content, style, name, type}) {
        return {
            _id: uuid(),
            createdAt: moment().format("YYYY-MM-DD HH:mm:ss"),
            updatedAt: moment().format("YYYY-MM-DD HH:mm:ss"),
            commonScripts,
            commonStyles,
            scripts,
            content,
            style,
            name,
            type
        }
    }

    async unlockCommonBoard(tree) {
        const connectedBoardId = tree.data['data-id'];
        delete tree.data.checked;
        const boardId = this.connectedBoardInfoMap[connectedBoardId].commonBoardOrigin;
        const beforeConnectedBoardInfo = Object.assign(this.connectedBoardInfoMap[connectedBoardId], {
            connectedBoardId,
            _id: boardId
        });
        const response = await axios.get(`/board/unlock?boardId=${boardId}`);
        /** @type {ResponseSuccessForm} */
        const result = response.data;
        let component = this.componentList.find(comp => comp.attributes.attributes['data-id'] === connectedBoardId);

        /** @type {ResponseBoard} */
        const board = result.data['board'];
        const cssRules = this.getOneDepthComponentCssRules(component.getChildAt(0));
        for (const cssRule of cssRules) {
            cssRule.destroy();
        }
        component.destroy();
        if (board.isSet) {
            board.type = 'CLIP_SET';
            tree.data.type = 'CLIP_SET';
            component = await this.addClipBoardSet(board, connectedBoardId)
        } else {
            board.type = 'CLIP';
            tree.data.type = 'CLIP';
            component = await this.addClipBoard(board, connectedBoardId);
        }
        const afterConnectedBoardInfo = board;
        const {content, style} = await this.getBoardContentsAndStyles(component, board.type);
        afterConnectedBoardInfo.content = content;
        afterConnectedBoardInfo.style = style;
        afterConnectedBoardInfo.connectedBoardId = connectedBoardId;
        this.refreshComponentList();
        this.editor.Commands.run("reload-scripts");
        getUndoManager().add(new UnlockCommonBoardStack(beforeConnectedBoardInfo, afterConnectedBoardInfo));
        this.relocateHighlighter();
    }

    /**
     * 공통보드 편집 모달에서 공통보드 추가하기 기능을 사용하였을 때 호출되는 함수
     * @param {number} index 보드가 들어갈 인덱스 넘버
     * @return {Promise<void>}
     */
    async addCommonModalBoard(index) {
        const board = this.commonBoardEditorModal.data
        let component;
        if (board.type === 'COMMON') {
            component = await this.addCommonBoard(board);
        }
        if (index !== undefined) {
            if (Array.isArray(component)) {
                for (const comp of component) {
                    this.changeComponentListOrder(comp, index);
                    index++;
                }
            } else {
                this.changeComponentListOrder(component, index);
            }
        }
    }

    /**
     * 에디터에 클립보드를 추가하는 함수
     * @param {ResponseBoard|ConnectedBoardInfo} board 서버로부터 받아온 클립보드 정보
     * @param {string?} customId 저장되지 않은 연결보드에 발급되는 임시 고유 아이디
     * @return {Promise<import('grapesjs').Component|import('grapesjs').Component[]>}
     */
    async addClipBoard(board, customId) {
        const editor = this.editor;
        let {type, name, commonScripts, commonStyles, content, style, scripts} = board;
        const boardId = customId || uuid();
        this.connectedBoardInfoMap[boardId] = {
            commonStyles, commonScripts, scripts, commentCount: 0, type, name, content, style
        };
        await this.addPublicInfoFromBoard(commonStyles, commonScripts, scripts);

        let contentObject = await HTMLToJSON(content);
        const randomStringMap = customId ? board.url ? this.convertHtmlAndAddAssets(contentObject, customId) : this.getConnectedBoardRandomString(contentObject) : this.convertHtmlAndAddAssets(contentObject, boardId);
        // customId만 있고 url이 없으면 HTML에 이미 XeRqh가 있음. CSS 재변환 시 셀렉터에 랜덤 클래스가 중복됨
        // (addClipBoardSet과 동일 가드 — 그룹 해제/복제/undo 복원 경로)
        if (!customId || board.url) {
            const styleObject = this.convertCssWithRandomClassName(style, randomStringMap);
            style = toCSS(styleObject);
        }
        content = await JSONToHTML(contentObject);
        content = content.replaceAll("<br></br>", "<br/>")
        content = `<div id="${createId(5)}" data-id="${boardId}" data-board-type="${type}" data-name="${name}" class="connected_board_wrapper">${content}</div>`
        const wrapComponent = editor.DomComponents.addComponent(content);
        runInAction(() => {
            this.addStyle(style, wrapComponent.getChildAt(0));
            this.componentList = editor.DomComponents.getComponents().models;
        });
        // wrapComponent.set("locked", true);
        if (wrapComponent.getChildAt(0).getClasses().includes('info_wrap')) {
            wrapComponent.set("locked", true);
        }
        return wrapComponent;
    }

    /**
     * 에디터에 클립보드 세트에 해당하는 보드들을 추가
     * 세트의 경우 보드 정보 여러개가 JSON 으로 묶여 있어 파싱한 후에 여러개의 보드로 나누어 추가
     * @param {ResponseBoard|ConnectedBoardInfo} board 서버로부터 받아온 클립보드세트 정보
     * @param {string?} customId 저장되지 않은 연결보드에 발급되는 임시 고유 아이디
     * @return {Promise<import('grapesjs').Component>}
     */
    async addClipBoardSet(board, customId) {
        let {type, name, commonScripts, commonStyles, content, style, scripts} = board;
        const styles = JSON.parse(style);
        const contents = JSON.parse(content);
        // content 개수 기준으로 scripts* 길이를 맞춰 깨진 CLIP_SET도 로드 가능하게 함
        ({scripts, commonScripts, commonStyles} = this.syncClipSetMetaArrays(scripts, commonScripts, commonStyles, contents.length));
        const boardId = customId || uuid();
        this.connectedBoardInfoMap[boardId] = {
            commonStyles, commonScripts, scripts, commentCount: 0, type, name, content, style
        };
        let combineContent = ''
        const randomStringMapList = [];
        for (let i = 0; i < contents.length; i++) {
            await this.addPublicInfoFromBoard(
                JSON.parse(commonStyles[i] || '[]'),
                JSON.parse(commonScripts[i] || '[]'),
                JSON.parse(scripts[i] || '[]')
            );
            const childContent = contents[i];
            let contentObject = await HTMLToJSON(childContent);
            const randomStringMap = customId ? board.url ? this.convertHtmlAndAddAssets(contentObject, customId) : this.getConnectedBoardRandomString(contentObject) : this.convertHtmlAndAddAssets(contentObject, boardId);
            randomStringMapList.push(randomStringMap);
            const newContent = await JSONToHTML(contentObject);
            combineContent += newContent;
        }
        combineContent = combineContent.replaceAll("<br></br>", "<br/>")
        combineContent = `<div id="${createId(5)}" data-id="${boardId}" data-board-type="${type}" data-name="${name}" class="connected_board_wrapper">${combineContent}</div>`
        const wrapComponent = this.editor.DomComponents.addComponent(combineContent);
        const childComponents = wrapComponent.components().models;
        for (let i = 0; i < contents.length; i++) {
            const childComponent = childComponents[i];
            if (!childComponent) continue;
            let childStyle = styles[i] || '';
            if (childStyle && (!customId || board.url)) {
                const styleObject = this.convertCssWithRandomClassName(childStyle, randomStringMapList[i]);
                childStyle = toCSS(styleObject);
            }
            if (childStyle) this.addStyle(childStyle, childComponent);
            if (childComponent.getClasses().includes('info_wrap')) {
                childComponent.set("locked", true);
            }
        }
        this.componentList = this.editor.DomComponents.getComponents().models;
        // runInAction(() => {
        //
        // })
        return wrapComponent;
    }

    /**
     * 에디터에 공통보드를 추가하는 함수
     * @param {ResponseBoard} board 서버로부터 받아온 공통보드 정보
     * @param {string?} customId 저장되지 않은 연결보드의 경우 임시 고유 아이디
     * @return {Promise<import('grapesjs').Component|import('grapesjs').Component[]>}
     */
    async addCommonBoard(board, customId) {
        let {type, name, commonScripts, commonStyles, scripts, url, content, style} = board;
        const boardId = board._id;
        const createdId = customId || uuid();
        this.connectedBoardInfoMap[createdId] = {
            commonStyles,
            commonScripts,
            scripts,
            commentCount: 0,
            type,
            name,
            url,
            commonBoardOrigin: board._id,
            content,
            style
        };
        this.addCommonBoardSize(this.editor, url);
        const html = this.getCommonBoardContent(createdId, boardId, url, name);
        const wrapComponent = this.editor.DomComponents.addComponent(html);
        runInAction(() => {
            this.componentList = this.editor.DomComponents.getComponents().models;
        });
        return wrapComponent;
    }

    /**
     * 보드의 공용 정보들을 에디터에 입력하는 함수
     * @param {string[]} commonStyles
     * @param {string[]} commonScripts
     * @param {string[]} scripts
     */
    async addPublicInfoFromBoard(commonStyles, commonScripts, scripts) {
        if (commonStyles && commonStyles.length > 0) {
            for (const commonStyle of commonStyles) {
                if (!this.pageInfo.commonStyles.includes(commonStyle)) {
                    this.pageInfo.commonStyles.push(commonStyle);
                }
            }
        }
        if (commonScripts && commonScripts.length > 0) {
            for (const commonScript of commonScripts) {
                if (!this.pageInfo.commonScripts.includes(commonScript)) {
                    this.pageInfo.commonScripts.push(commonScript);
                }
            }
        }
        if (scripts && scripts.length > 0) {
            for (const script of scripts) {
                if (!this.pageInfo.scripts.includes(script)) {
                    this.pageInfo.scripts.push(script);
                }
            }
        }
    }

    async deletePublicInfoFromBoard(connectedBoardIds, isDrop = true) {
        const deleteInfos = [];
        for (const connectedBoardId of connectedBoardIds) {
            this.boardCommentMap = {selector: undefined, boardType: undefined};
            const pageCommonScripts = [];
            const pageCommonStyles = [];

            for (const cId in this.connectedBoardInfoMap) {
                const connectedBoardInfo = this.connectedBoardInfoMap[cId];
                if (cId === connectedBoardId) {
                    if (connectedBoardInfo.type === 'COMMON') {
                        this.deleteCommonBoardSize(this.editor, connectedBoardInfo.url)
                    }
                }

                connectedBoardInfo.commonScripts.forEach(commonScript => {
                    if (connectedBoardInfo.type === 'CLIP_SET') {
                        const commonScripts = JSON.parse(commonScript);
                        commonScripts.forEach(cs => {
                            if (!pageCommonScripts.includes(cs)) pageCommonScripts.push(cs)
                        })
                    } else {
                        if (!pageCommonScripts.includes(commonScript)) pageCommonScripts.push(commonScript)
                    }

                });
                connectedBoardInfo.commonStyles.forEach(commonStyle => {
                    if (connectedBoardInfo.type === 'CLIP_SET') {
                        const commonStyles = JSON.parse(commonStyle);
                        commonStyles.forEach(cs => {
                            if (!pageCommonStyles.includes(cs)) pageCommonStyles.push(cs)
                        })
                    } else {
                        if (!pageCommonStyles.includes(commonStyle)) pageCommonStyles.push(commonStyle)
                    }

                });
            }
            const connectedBoardInfo = Object.assign(this.connectedBoardInfoMap[connectedBoardId], {
                connectedBoardId: connectedBoardId
            });
            const comp = this.componentList.find(component => component.attributes.attributes['data-id'] === connectedBoardId);
            const convertResult = await this.getBoardContentsAndStyles(comp, comp.attributes.attributes['data-board-type']);
            connectedBoardInfo.content = convertResult.content;
            connectedBoardInfo.style = convertResult.style
            deleteInfos.push({connectedBoardInfo, componentIndex: comp.index()});
            if (isDrop) {
                delete this.connectedBoardInfoMap[connectedBoardId];
            }
            this.pageInfo.commonScripts = pageCommonScripts;
            this.pageInfo.commonStyles = pageCommonStyles;

            // wrapper가 아니라 1depth(CLIP) / 자식들(CLIP_SET)에 연결된 규칙을 제거
            this.removeConnectedBoardCssRules(comp);
        }
        return deleteInfos;
    }

    /**
     * 연결보드 제거 시 CssComposer에 남은 스타일 규칙을 정리하는 함수
     * CLIP: 1depth 자식, CLIP_SET: 모든 1depth 자식
     * @param {import('grapesjs').Component} component connected_board_wrapper
     */
    removeConnectedBoardCssRules(component) {
        if (!component) return;
        const boardType = component.attributes?.attributes?.['data-board-type'];
        const removeRules = (target) => {
            if (!target) return;
            this.getOneDepthComponentCssRules(target).forEach((cssRule) => {
                this.editor.CssComposer.remove(cssRule);
            });
        };
        if (boardType === 'CLIP_SET') {
            component.components().models.forEach(removeRules);
            return;
        }
        removeRules(component.getChildAt(0));
    }

    /**
     * 공통보드의 사이지를 document 에 추가해주는 함수
     * @param {import('grapesjs').Editor} editor 에디터
     * @param {string} url 스타일 css 주소
     */
    addCommonBoardSize(editor, url) {
        url = url.replace(/\+/g, '%2B');
        const urlSplitList = url.split('.');
        urlSplitList.pop();
        urlSplitList.push('css');
        const cssUrl = encodeURI(urlSplitList.join('.'));
        let doc = editor.Canvas.getDocument();
        const linkEl = doc.createElement('link');
        linkEl.rel = 'stylesheet';
        linkEl.href = cssUrl;
        linkEl.type = "text/css"
        doc.head.appendChild(linkEl)
    }

    deleteCommonBoardSize(editor, url) {
        const urlSplitList = url.replace(/\+/g, '%2B').split('.');
        urlSplitList.pop();
        urlSplitList.push('css');
        const cssUrl = encodeURI(urlSplitList.join('.'));
        let doc = editor.Canvas.getDocument();
        const removeList = [];
        for (const child of doc.head.children) {
            if (child.href === cssUrl) {
                removeList.push(child);
                break;
            }
        }
        removeList.forEach(child => doc.head.removeChild(child));
    }

    /**
     * 연결보드의 컨텐츠에서 변환된 오브젝트로부터 클래스명에 포함된 랜덤 난수를 알아내는 재귀함수
     * @param {object} object Content html 오브젝트
     * @param {Record<string,string>} randomStringMap
     * @return {Record<string,string>}
     */
    getConnectedBoardRandomString(object, randomStringMap = {}) {
        if (typeof object !== 'string' && object['type'] !== "br") {
            if (object['attributes'] && object['attributes'].class && object['attributes'].class !== 'on') {
                const convertedClassName = object['attributes'].class;
                const classNameSplitList = convertedClassName.split(" ");
                if (classNameSplitList.length > 1) {
                    const deleteIndexes = [];
                    for (let i = classNameSplitList.length - 1; i >= 0; i--) {
                        if (classNameSplitList[i].startsWith('XeRqh')) {
                            deleteIndexes.push(i);
                        }
                    }
                    for (const deleteIndex of deleteIndexes) {
                        const originClassName = classNameSplitList[deleteIndex - 1];
                        randomStringMap[originClassName] = classNameSplitList.splice(deleteIndex, 1)[0]
                    }
                }
            }
            if (object['content'] && typeof Array.isArray(object['content'])) {
                for (const element of object['content']) {
                    this.getConnectedBoardRandomString(element, randomStringMap);
                }
            }
        }
        return randomStringMap;
    }

    /**
     * 보드를 에디터에 추가할 때 html 의 클래스에 랜덤난수를 넣어주는 재귀 함수
     * 모든 하위 요소들을 돌면서 동일한 클래스명의 경우 동일한 랜덤난수로 변경해준다.
     * @param {object} object content html 오브젝트
     * @param {string} boardId 보드의 고유 아이디
     * @param {Record<string,string>} alreadyConvertClass 이전에 변환한 클래스인지 확인하는 용도의 맵
     * @return {Record<string,string>}
     */
    convertHtmlAndAddAssets(object, boardId, alreadyConvertClass = {}) {
        if (typeof object !== 'string' && object['type'] !== "br") {
            if (object['attributes'] && object['attributes'].class) {
                if (object['attributes'].class !== 'on') {
                    const classNameSplitList = object['attributes'].class.split(' ');
                    for (const originClassName of classNameSplitList) {
                        if (['on', 'pc', 'mo'].includes(originClassName)) continue;
                        if (!alreadyConvertClass[originClassName]) {
                            const randomString = `XeRqh${createId(6)}`;
                            alreadyConvertClass[originClassName] = randomString;
                            object['attributes'].class = object['attributes'].class.replace(originClassName, `${originClassName} ${randomString}`);
                        } else {
                            object['attributes'].class = object['attributes'].class.replace(originClassName, `${originClassName} ${alreadyConvertClass[originClassName]}`);
                        }
                    }
                }
            }
            if (object['type'] === "img") {
                if (!this.pageInfo.assets.includes(object.attributes.src)) {
                    this.pageInfo.assets.push(object.attributes.src);
                    this.editor.Assets.add(object.attributes.src);
                }
            }
            if (object['content'] && typeof Array.isArray(object['content'])) {
                for (const element of object['content']) {
                    this.convertHtmlAndAddAssets(element, boardId, alreadyConvertClass);
                }
            }
        }
        return alreadyConvertClass;
    }

    /**
     * 연결보드를 보드에 저장하기 전 클래스의 랜덤 난수를 제거하는 함수
     * 오브젝트 객체 자체를 변환하기 때문에 리턴값 없음
     * @param {object} object content html 오브젝트
     * @param {string[]} randomClasses 빠진 랜덤 클래스 이름들
     * @return {string[]}
     */
    convertHtmlWithoutRandomClassName(object, randomClasses = []) {
        if (typeof object !== 'string' && object['type'] !== "br") {
            if (object['attributes'] && object['attributes'].class && object['attributes'].class !== 'on') {
                const classNameSplitList = object['attributes'].class.split(" ");
                if (classNameSplitList.length > 1) {
                    let deleteIndexes = [];
                    for (let i = classNameSplitList.length - 1; i >= 0; i--) {
                        if (classNameSplitList[i].startsWith('XeRqh')) {
                            deleteIndexes.push(i);
                        }
                    }
                    for (const deleteIndex of deleteIndexes) {
                        const randomClass = classNameSplitList.splice(deleteIndex, 1);
                        randomClasses.push(randomClass[0]);
                    }
                }
                object['attributes'].class = classNameSplitList.join(" ");
            }
            if (object['content'] && typeof Array.isArray(object['content'])) {
                for (const element of object['content']) {
                    this.convertHtmlWithoutRandomClassName(element, randomClasses);
                }
            }
        }
        return randomClasses
    }

    /**
     * @typedef {Object} StyleInfo
     * @property {Record<string,StyleInfo>} children
     * @property {object} attributes
     */
    /**
     * html 의 클래스에 랜덤난수를 추가한 뒤 그에 맞게 스타일 클래스에도 랜덤난수를 추가해주는 함수
     * @param {string|object} style 스타일 정보
     * @param {Record<string,string>} randomStringMap {key: 원래 클래스 이름, value: 랜덤 난수}
     * @return {object}
     */
    convertCssWithRandomClassName(style, randomStringMap) {
        if (typeof style === 'string') {
            style = toJSON(style);
        }
        const newStyleObject = {children: {}, attributes: {}}
        for (const styleKey in style.children) {
            const styleKeySplitList = styleKey.split(",");
            for (const styleSelector of styleKeySplitList) {
                const styleSelectorSplitList = styleSelector.split(" ");
                const newClassNameList = [];
                for (const splitStyleSelectorOne of styleSelectorSplitList) {
                    if (!splitStyleSelectorOne.startsWith('.')) {
                        newClassNameList.push(splitStyleSelectorOne);
                        continue;
                    }

                    const styleSelectorSplitListTwo = splitStyleSelectorOne.split(":")
                    let styleClassNameString = styleSelectorSplitListTwo.shift();
                    const styleClassNameList = styleClassNameString.split(".");
                    const newStyleClassNameList = [];
                    for (const styleClassName of styleClassNameList) {
                        if (randomStringMap[styleClassName]) {
                            newStyleClassNameList.push(`${styleClassName}.${randomStringMap[styleClassName]}`);
                        } else {
                            newStyleClassNameList.push(`${styleClassName}`);
                        }
                    }
                    let newStyleClassName = newStyleClassNameList.join(".");
                    if (styleSelectorSplitListTwo.length > 0) {
                        styleSelectorSplitListTwo.forEach(state => newStyleClassName += `:${state}`);
                    }
                    newClassNameList.push(newStyleClassName);
                }
                const newClassName = newClassNameList.join(" ");
                if (Object.keys(style.children[styleKey].children).length > 0) {
                    newStyleObject.children[newClassName] = this.convertCssWithRandomClassName(style.children[styleKey], randomStringMap);
                } else {
                    newStyleObject.children[newClassName] = {
                        children: {},
                        attributes: style.children[styleKey].attributes
                    }
                }
            }
        }
        newStyleObject.attributes = style.attributes;
        return newStyleObject;
    }

    /**
     * 보드에 저장될 때 스타일의 랜덤난수를 제거해주는 함수
     * @param {StyleInfo|object[]} style 스타일 정보
     * @param {string[]} randomClasses 랜덤 클래스네임 리스트
     * @return {object}
     */
    convertCssWithoutRandomClassName(style, randomClasses = []) {
        if (Array.isArray(style)) {
            style = this.makeStyleObject(style);
        }
        const newStyleObject = {children: {}, attributes: {}}
        for (const styleKey in style.children) {
            let styleKeyWithOutRandomClassName = styleKey;
            for (const randomClass of randomClasses) {
                styleKeyWithOutRandomClassName = styleKeyWithOutRandomClassName.replaceAll(`.${randomClass}`, '');
            }
            // let originClassName = styleKeyWithOutState;
            // const styleKeySplitList = styleKeyWithOutState.split('.');
            // const addedClassList = [];
            // for (const str of styleKeySplitList) {
            //     if (randomClasses.includes(str)) {
            //         addedClassList.push(`.${str}`);
            //     }
            // }
            // addedClassList.forEach(randomClass => {
            //     originClassName = originClassName.replace(randomClass, '');
            // })
            // }
            if (Object.keys(style.children[styleKey].children).length > 0) {
                newStyleObject.children[styleKeyWithOutRandomClassName] = this.convertCssWithoutRandomClassName(style.children[styleKey], randomClasses);
            } else {
                newStyleObject.children[styleKeyWithOutRandomClassName] = {
                    children: {},
                    attributes: style.children[styleKey].attributes
                }
            }
        }
        newStyleObject.attributes = style.attributes;
        return newStyleObject;
    }

    /**
     * CssRule 을 object 로 변환한 리스트를 트리구조로 만들어주는 함수
     * @param {object[]} style CssRule 리스트
     * @return {StyleInfo}
     */
    makeStyleObject(style) {
        /** @type {StyleInfo} */
        const newStyle = {children: {}, attributes: {}};
        for (const styleObject of style) {
            let parent = newStyle.children;
            if (styleObject.mediaText) {
                if (!newStyle.children[`@media ${styleObject.mediaText}`]) {
                    newStyle.children[`@media ${styleObject.mediaText}`] = {children: {}, attributes: {}};
                }
                parent = newStyle.children[`@media ${styleObject.mediaText}`].children;
            }
            if (styleObject.selectorsAdd) {
                parent[styleObject.selectorsAdd] = {children: {}, attributes: styleObject.style};
            } else {
                let className = '';
                for (const selector of styleObject.selectors.models) {
                    className += `${selector.attributes.type === 1 ? '.' : '#'}${selector.id}`;
                }
                if (styleObject.state) {
                    className = className + `:${styleObject.state}`;
                }
                parent[className] = {children: {}, attributes: styleObject.style};
            }
        }
        return newStyle;
    }

    /**
     * 보드 저장을 위해 클래스명에서 랜덤 난수를 제거하는 함수
     * @param {import('grapesjs').Component} component 컴포넌트
     * @param {String} componentType 컴포넌트의 타입
     * @param {Boolean} withoutRandomClass 세이브용인지 아닌지에 대한 여부
     * @return {Promise<{style: string, content: string}>}
     */
    async getBoardContentsAndStyles(component, componentType, withoutRandomClass = false) {
        let content = '', style = '';
        if (componentType === 'CLIP_SET') {
            const childComponents = component.components().models;
            const styleList = [];
            const contentList = [];
            for (const childComponent of childComponents) {
                let childContent = childComponent.toHTML();
                const cssRules = this.getOneDepthComponentCssRules(childComponent).map(cssRule => cssRule.toJSON());
                const contentObject = await HTMLToJSON(childContent);
                const randomClasses = withoutRandomClass ? this.convertHtmlWithoutRandomClassName(contentObject) : this.getConnectedBoardRandomString(contentObject);
                const styleObject = withoutRandomClass ? this.convertCssWithoutRandomClassName(cssRules, randomClasses) : this.makeStyleObject(cssRules);
                const childStyle = toCSS(styleObject);
                childContent = await JSONToHTML(contentObject);
                childContent = childContent.replaceAll("<br></br>", "<br/>");
                contentList.push(childContent);
                styleList.push(childStyle);
            }
            content = JSON.stringify(contentList);
            style = JSON.stringify(styleList);
        } else {
            const childContent = component.getChildAt(0).toHTML();
            const cssRules = this.getOneDepthComponentCssRules(component.getChildAt(0)).map(cssRule => cssRule.toJSON());
            const contentObject = await HTMLToJSON(childContent);
            const randomClasses = withoutRandomClass ? this.convertHtmlWithoutRandomClassName(contentObject) : this.getConnectedBoardRandomString(contentObject);
            const styleObject = withoutRandomClass ? this.convertCssWithoutRandomClassName(cssRules, randomClasses) : this.makeStyleObject(cssRules);
            style = toCSS(styleObject);
            content = await JSONToHTML(contentObject);
            content = content.replaceAll("<br></br>", "<br/>");
        }

        return {content, style};
    }

    /**
     * 에디터에 등록된 최상위 컴포넌트들의 정렬을 바꾸고 해당 인덱스에 새로운 컴포넌트를 등록할 때 사용되는 함수
     * @param {Component | Component[]} item 새로운 컴포넌트
     * @param {number} index 새로운 컴포넌트가 위치할 인덱스
     */
    changeComponentListOrder(item, index) {
        const components = this.editor.DomComponents.getComponents().models;
        if (!item) {
            this.refreshComponentList(components);
            return;
        }
        const filtered = components.filter((curr) => curr.attributes.attributes['data-id'] !== item.attributes.attributes['data-id']);
        filtered.splice(index, 0, item);
        this.refreshComponentList(filtered);
        this.editor.Commands.run('reload-scripts');
    }

    /**
     * 사이드바에서 보드를 드래그앤드롭으로 에디터에 추가할 때 에디터에 등록되어 있는 연결보드들을 잠금하는 함수
     * @param {boolean} locked 잠금 여부
     * @return void
     */
    lockClipConnectedBoard(locked) {
        for (const component of this.componentList) {
            component.set("locked", locked)
        }
        this.editor.Canvas.refresh();
    }

    /**
     * 파일 위에 마우스가 호버되었을 때 Queue 세팅 함수
     * @param {string} id 파일의 고유 아이디
     * @return {Promise<void>}
     */
    async onFileHoverEvent(id) {
        if (id !== "") {
            await this.queue.push(async () => {
                await this.fileHoverView(id);
            });
        }
    }

    /**
     * 파일 위에 마우스가 호버되었을 때 미리보기 노출을 결정하는 함수
     * @param {string} id 파일의 고유 아이디
     * @return {Promise<void>}
     */
    async fileHoverView(id) {
        if (id === '') return;
        const response = await axios.get(`/board?fileId=${id}`);
        /** @type {ResponseBoard} */
        const boardData = response.data?.data['board'];
        if (boardData.type === "TEMPLATE" || boardData.type === "CLIP" || boardData.type === "CLIP_SET") {
            let headString = `<script type="text/javascript" src="https://ajax.googleapis.com/ajax/libs/jquery/3.7.1/jquery.min.js"></script>`;
            let content = '', style = '';
            let [commonScripts, commonStyles, scripts] = [[], [], []];
            if (boardData.isSet) {
                content += '<div class="content-wrapper">'
                const parsedContent = JSON.parse(boardData.content);
                const parsedStyle = JSON.parse(boardData.style);
                const synced = this.syncClipSetMetaArrays(boardData.scripts, boardData.commonScripts, boardData.commonStyles, parsedContent.length);
                for (let i = 0; i < parsedContent.length; i++) {
                    const parsedCommonScripts = JSON.parse(synced.commonScripts[i] || '[]');
                    for (const commonScript of parsedCommonScripts) {
                        if (!commonScripts.includes(commonScript)) commonScripts.push(commonScript);
                    }
                    const parsedCommonStyles = JSON.parse(synced.commonStyles[i] || '[]');
                    for (const commonStyle of parsedCommonStyles) {
                        if (!commonStyles.includes(commonStyle)) commonStyles.push(commonStyle);
                    }
                    const parsedScripts = JSON.parse(synced.scripts[i] || '[]');
                    for (const script of parsedScripts) {
                        if (!scripts.includes(script)) scripts.push(script);
                    }
                    content += parsedContent[i] || '';
                    style += parsedStyle[i] || '';
                }
                content += '</div>'
            } else {
                commonScripts = boardData.commonScripts;
                commonStyles = boardData.commonStyles;
                scripts = boardData.scripts;
                content = boardData.content;
                style = boardData.style;
            }
            const combinedScripts = [...commonScripts, ...scripts];
            combinedScripts.forEach(script => {
                headString += `<script type="text/javascript" src="${script}"></script>`;
            });
            commonStyles.forEach(style => {
                headString += `<link rel="stylesheet" type="text/css" href="${style}"></link>`;
            });
            this.layerHoverPreview = `
                    <!DOCTYPE html>
                    <html style="overflow: hidden;">
                        <head>${headString}</head>
                        <body><style>${style}\n body{overflow: hidden !important;}\n img{max-width: 100%;}\n 
                        .info_wrap_hidden_description {display: block;font-size: 45px;text-align: center;color:rgba(195, 195, 195, 1);border:1px solid;margin-top:150px;}\n 
                        .detail_video01 {width: 100%;}\n
                        @media screen and (max-width: 660px) {.info_wrap_hidden_description {display: block;font-size: 25px;}}</style>
                        ${content}</body>
                    </html>
                `;
        }
    }

    /**
     * 파일 위에 마우스가 호버되었을 때 미리보기 노출을 초기화하는 함수
     * @return {void}
     */
    fileHoverOut() {
        this.queue.push(() => {
            this.layerHoverPreview = undefined;
        })

    }

    /**
     * 공통보드 편집 모달에 공통보드 데이터를 세팅하는 함수
     * @param {string} fileId 파일의 고유 아이디
     * @param {string} path 공통보드 파일의 경로
     * @return {Promise<void>}
     */
    async setCommonBoardEditorData(fileId, path) {
        if (fileId !== "") {
            const response = await axios.get(`/board?fileId=${fileId}`);
            /** @type {ResponseSuccessForm} */
            const responseData = response.data;
            /** @type {ResponseBoard} */
            const boardData = responseData.data['board'];
            if (boardData.type === "COMMON") {
                boardData.url = boardData.url.replace(/\+/g, '%2B');
                this.commonBoardEditorModal.data = boardData;
                this.commonBoardEditorModal.path = path;
                this.commonBoardEditorModal.ratio = boardData.ratio;

                this.commonBoardEditorModal.state = true;
            }
        } else {
            this.commonBoardEditorModal.state = false;
            this.commonBoardEditorModal.data = {};
            this.commonBoardEditorModal.path = undefined;
            this.commonBoardEditorModal.ratio = {};
            this.commonBoardEditorModal.component = undefined;
            this.commonBoardEditorModal.styleList = [];
        }
    }

    /**
     * 공통보드 편집모달의 에디터에 데이터를 세팅하는 함수
     * @return {Promise<void>}
     */
    commonBoardEditorModalSetData(boardData = undefined) {
        const commonEditor = this.commonEditor;
        let commonBoardModalData = this.commonBoardEditorModal.data;
        if (boardData) {
            commonBoardModalData = boardData;
            this.commonBoardEditorModal.data = boardData;
        }
        let contentString = "";
        if (commonBoardModalData.isSet || commonBoardModalData.type === "CLIP_SET") {
            const contentList = JSON.parse(commonBoardModalData.content);
            for (const content of contentList) {
                contentString += content;
            }
        } else {
            contentString = commonBoardModalData.content;
        }
        const wrapperId = createId(10);
        this.commonBoardEditorModal.component = commonEditor.DomComponents.addComponent(
            `<div id="${createId(5)}" data-id="${commonBoardModalData._id}" data-board-type="COMMON" data-name="${commonBoardModalData.name}" class="connected_board_wrapper prev-detail-component" id="${wrapperId}">
                        ${contentString}
                        </div>`);
        if (commonBoardModalData.isSet || commonBoardModalData.type === "CLIP_SET") {
            const styleList = JSON.parse(commonBoardModalData.style);
            const childComponents = this.commonBoardEditorModal.component.components().models;
            for (const childComponent of childComponents) {
                this.addStyle(styleList[childComponent.index()], childComponent);
            }
        } else {
            this.addStyle(commonBoardModalData.style, this.commonBoardEditorModal.component.getChildAt(0));
        }

        commonEditor.setDevice('Desktop');
        this.commonBoardEditorModal.initHtml = this.getContentHtml([this.commonBoardEditorModal.component]);
        this.commonBoardEditorModal.initCss = this.getContentCss([this.commonBoardEditorModal.component]);
        for (const asset of this.pageInfo.assets) {
            this.commonEditor.Assets.add(asset);
        }

    }

    /**
     * 보드 정보 탭에서 선택된 연결보드를 세팅하는 함수
     * @param {import('grapesjs').Component} component 선택된 컴포넌트
     */
    setBoardCommentMap(component) {
        const higherComponent = findParent(component);
        const connectedBoardInfo = this.connectedBoardInfoMap[higherComponent.attributes.attributes['data-id']];
        if (component === higherComponent) {
            this.boardCommentMap.selector = this.getComponentSelector(component.getChildAt(0)).selector;
        } else {
            const oneDepthComponent = findOneDepthParent(component);
            this.boardCommentMap.selector = this.getComponentSelector(oneDepthComponent).selector;
        }
        // if (connectedBoardInfo.type === 'CLIP_SET') {
        //     if (component === higherComponent) {
        //         this.boardCommentMap.selector = this.getComponentSelector(component.getChildAt(0)).selector;
        //     } else {
        //         this.boardCommentMap.selector = this.getComponentSelector(component).selector;
        //     }
        //     // const childComponents = component.components().models;
        //     // this.boardCommentMap.selector = childComponents.map(childComponent => this.getComponentSelector(childComponent).selector);
        // } else {
        //     this.boardCommentMap.selector = this.getComponentSelector(component).selector;
        // }

        this.boardCommentMap.boardType = connectedBoardInfo.type;
    }

    /**
     * 연결보드를 에디터에서 제거하는 함수
     * @param {number} index 연결 보드 최상위 컴포넌트
     * @param {boolean} refresh 보드 리로드 환경
     */
    async dropConnectedBoard(index, refresh) {
        const connectedBoardId = this.boardTree[index].data['data-id'];
        const tree = this.boardTree.find(node => node.data['data-id'] === connectedBoardId);
        const childNodeList = this.boardTree.filter(node => node.parent === tree.id);
        const deleteTrees = [tree, ...childNodeList];
        deleteTrees.forEach(node => delete node.data.checked);
        this.boardTree = this.boardTree.filter(node => node.parent !== tree.id && node.id !== tree.id);
        const deleteConnectedBoardIds = deleteTrees.map(node => node.data['data-id']);
        const deleteInfos = await this.deletePublicInfoFromBoard(deleteConnectedBoardIds);
        this.componentList.filter((component) => deleteConnectedBoardIds.includes(component.attributes.attributes['data-id'])).forEach(comp => {
            comp.destroy();
        });
        this.relocateHighlighter();
        if (refresh) {
            this.refreshComponentList();
            this.editor.Commands.run('reload-scripts');
        }
        return {deleteInfos, deleteTrees};

    }

    /**
     * 에디터에 등록된 모든 컴포넌트를 임시저장했다가 새로 등록함으로써 다시 로드하게 하는 함수
     * @param {import('grapesjs').Component[]?} list
     */
    refreshComponentList(list) {
        if (list) {
            this.componentList = list;
        } else {
            this.componentList = this.editor.DomComponents.getComponents().models;
        }
        /** @type {string[]} */
        // const newConnectedBoardIds = this.boardTree.map(tree => tree.data['data-id']);
        /** @type {Record<string,string}>} */
        const prevComponentMap = {};
        // eslint-disable-next-line array-callback-return
        this.componentList.map((component) => {
            const boardId = component.attributes.attributes['data-id'];
            if (this.connectedBoardInfoMap[boardId]?.type === 'COMMON') {
                prevComponentMap[boardId] = this.getCommonBoardContent(boardId, this.connectedBoardInfoMap[boardId].commonBoardOrigin, this.connectedBoardInfoMap[boardId].url, this.connectedBoardInfoMap[boardId].name);
            } else if (this.connectedBoardInfoMap[boardId]?.type === 'CLIP_SET') {
                prevComponentMap[boardId] = component.toHTML();
            } else {
                prevComponentMap[boardId] = component.toHTML();
            }
        });
        if (this.commonBoardEditorModal.state) {
            this.commonEditor.DomComponents.clear();
            this.componentList = [];
            for (const tree of this.boardTree) {
                this.commonEditor.DomComponents.addComponent(prevComponentMap[tree.data['data-id']])
            }
            this.componentList = this.commonEditor.DomComponents.getComponents().models;
        } else {
            this.editor.DomComponents.clear();
            for (const tree of this.boardTree) {
                const connectedBoardId = tree.data['data-id'];
                const addedComponent = this.editor.DomComponents.addComponent(prevComponentMap[connectedBoardId]);
                if (tree.data.type !== 'TOGGLE') {
                    if (tree.parent !== 0) {
                        addedComponent.addClass('edit-toggle-desc');
                        addedComponent.removeClass('prev-detail-component');
                    } else {
                        addedComponent.removeClass('edit-toggle-desc');
                        addedComponent.addClass('prev-detail-component');
                    }
                }
                if (this.connectedBoardInfoMap[connectedBoardId].type === 'TOGGLE') {
                    addedComponent.set("locked", true);
                } else if (this.connectedBoardInfoMap[connectedBoardId].type === 'CLIP_SET') {
                    const childComponents = addedComponent.components().models;
                    childComponents.forEach(childComponent => {
                        if (childComponent.getClasses().includes('info_wrap')) {
                            childComponent.set("locked", true);
                        }
                    })
                } else {
                    if (addedComponent.getChildAt(0) && addedComponent.getChildAt(0).getClasses().includes('info_wrap')) {
                        addedComponent.set("locked", true);
                    }
                }
            }
            this.componentList = this.editor.DomComponents.getComponents().models;
        }
    }

    /**
     * 에디터의 내용들을 모아서 하나의 html 파일 형태로 리턴하는 함수
     * 미리보기 기능에서 사용
     * @return {Promise<{content:string,subContent:string,captureContent:string}>}
     */
    async mergeToHtml() {
        let totalHeadString = `<meta charset="utf-8"><script type="text/javascript" src="https://ajax.googleapis.com/ajax/libs/jquery/3.7.1/jquery.min.js"></script><script src="https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js"></script><link href="https://fonts.googleapis.com/css?family=Noto+Sans+KR:100,300,400,500,700,900&amp;display=swap&amp;subset=korean" rel="stylesheet">`;
        let commonStyleHeadString = ``;
        for (const commonStyle of this.pageInfo.commonStyles) {
            totalHeadString += `<link href="${commonStyle}" rel="stylesheet">`;
            commonStyleHeadString += `<link href="${commonStyle}" rel="stylesheet">`
        }
        const scripts = [...this.pageInfo.commonScripts, `${import.meta.env.VITE_CLOUDFRONT_URL}/scripts/iframeResize.js`, ...this.pageInfo.scripts];
        for (const script of scripts) {
            totalHeadString += `<script type="text/javascript" src="${script}"></script>`;
        }
        let totalStyle = `
            * {font-family: "Gilroy", "Noto Sans KR", Malgun Gothic, "맑은 고딕", Dotum, "돋움", sans-serif;}
                            .prev-detail-component {padding: 0 56px} 
                            .iv-tab-toggle.active h1::after {transform: translateY(-50%) rotate(180deg);} 
                            .iv-tab-toggle.active .toggle-desc{display:block !important;}
                            @media screen and (max-width: 660px) {.prev-detail-component {padding: 0 0} }
        `, totalContent = ``, captureContent = ``;
        const componentBoardTree = [];
        for (let i = 0; i < this.boardTree.length; i++) {
            const tree = this.boardTree[i];
            const wrapComponent = this.editor.DomComponents.getComponents().models[i];
            if (tree.parent === 0) {
                componentBoardTree.push({
                    id: tree.id,
                    data: tree.data,
                    component: wrapComponent,
                    children: [],
                    show: tree.showAccordion
                });
            } else {
                const parent = componentBoardTree.find(cbt => cbt.id === tree.parent);
                parent.children.push({id: tree.id, data: tree.data, component: wrapComponent});
            }
        }
        for (const componentInfo of componentBoardTree) {
            if (componentInfo.data.type === 'TOGGLE') {
                const component = componentInfo.component.getChildAt(0);
                let componentContent = component.toHTML();
                let className = "";
                let inLineStyle = "";
                let fitGuideStyle = "";
                className = `iv-tab-toggle`;
                if (componentInfo.data.isSize) className += ' iv-tab-size-guide vreview-personal-widget-btn';
                if (componentInfo.data.isColor) className += ' prd_info'
                if (componentInfo.show) {
                    inLineStyle = 'style="display:block"';
                } else {
                    inLineStyle = 'style="display:none"';
                }
                if (componentInfo.data.isFitGuide) {
                    fitGuideStyle = 'style="display:none"';
                }
                if (componentInfo.data.isFitGuide) className += ' fit_guide';
                totalContent += `<div class="${className}" data-id="${componentInfo.data['data-id']}" ${fitGuideStyle}>${componentContent}${componentInfo.children.length > 0 ? `<div class="toggle-desc" ${inLineStyle}>` : ''}`;
                captureContent += `<div class="${className}${className === 'iv-tab-toggle' ? ' active' : ''}" data-id="${componentInfo.data['data-id']}">${componentContent}${componentInfo.children.length > 0 ? '<div class="toggle-desc">' : ''}`
                for (const childInfo of componentInfo.children) {
                    const {headString, content, style} = await this.convertComponentToHtml(childInfo.component);
                    totalHeadString += headString;
                    totalContent += content;
                    captureContent += content
                    totalStyle += style;
                }
                totalContent += `${componentInfo.children.length > 0 ? '</div>' : ''}</div>`;
                captureContent += `${componentInfo.children.length > 0 ? '</div>' : ''}</div>`;
            } else {
                const {headString, content, style} = await this.convertComponentToHtml(componentInfo.component);
                totalHeadString += headString;
                totalContent += `<div class="prev-detail-component">${content}</div>`;
                captureContent += `<div class="prev-detail-component">${content}</div>`;
                totalStyle += style;
            }
        }
        return {
            content: `
                <!DOCTYPE html>
                <html style="overflow: hidden;">
                    <head>${totalHeadString}</head>
                    <body style="margin: 0;"><style>.prev-detail-component {padding: 0 56px} .info_wrap_hidden_description {display: block;font-size: 45px;text-align: center;color:rgba(195, 195, 195, 1);border:1px solid;margin-top:150px;} @media screen and (max-width: 660px) {.prev-detail-component {padding: 0 0} .info_wrap_hidden_description {font-size:25px}}${totalStyle}</style><div class="layout_24FW">${totalContent}</div>
                    </body>
                </html>
            `,
            subContent: `${commonStyleHeadString}<style>#prdDetail {${totalStyle}}</style><div id="layout_24FW">${prettify(totalContent)}</div>`,
            captureContent: `
                    <head>${totalHeadString}</head>
                    <body style="margin: 0;">
                        <style>
                            ${totalStyle}
                        </style>
                        <div class="layout_24FW">${captureContent}</div>
                    </body>
                `
        };
    }

    async getFitGuideHtml() {
        let totalStyle = ``, totalContent = `<div class="fit_guide">`,
            totalHeadString = `<meta charset="utf-8"><script type="text/javascript" src="https://ajax.googleapis.com/ajax/libs/jquery/3.7.1/jquery.min.js"></script><script src="https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js"></script><link href="https://fonts.googleapis.com/css?family=Noto+Sans+KR:100,300,400,500,700,900&amp;display=swap&amp;subset=korean" rel="stylesheet">`;
        const fitGuideTree = this.boardTree.find(tree => tree.data.isFitGuide);
        if (!fitGuideTree) {
            return '';
        }
        const commonScripts = [], scripts = [], commonStyles = [];
        for (let i = 0; i < this.boardTree.length; i++) {
            const tree = this.boardTree[i];
            const wrapComponent = this.editor.DomComponents.getComponents().models[i];
            if (tree.parent === fitGuideTree.id) {
                const {headString, content, style} = await this.convertComponentToHtml(wrapComponent);
                totalHeadString += headString;
                for (const commonScript of this.connectedBoardInfoMap[wrapComponent.attributes.attributes['data-id']].commonScripts) {
                    if (!commonScripts.includes(commonScript)) commonScripts.push(commonScript);
                }
                for (const commonStyle of this.connectedBoardInfoMap[wrapComponent.attributes.attributes['data-id']].commonStyles) {
                    if (!commonStyles.includes(commonStyle)) commonStyles.push(commonStyle);
                }
                for (const script of this.connectedBoardInfoMap[wrapComponent.attributes.attributes['data-id']].scripts) {
                    if (!scripts.includes(script)) scripts.push(script);
                }
                totalHeadString += headString;
                totalContent += content;
                totalStyle += style;
            }
        }
        for (const commonStyle of commonStyles) {
            totalHeadString += `<link href="${commonStyle}" rel="stylesheet">`;
        }
        for (const commonScript of commonScripts) {
            totalHeadString += `<script type="text/javascript" src="${commonScript}"></script>`;
        }
        for (const script of scripts) {
            totalContent += `<script type="text/javascript" src="${script}"></script>`;
        }
        totalContent += '</div>'
        return `
            <!DOCTYPE html>
            <html style="overflow: hidden;">
                <head>${totalHeadString}</head>
                <body style="margin: 0;">
                    <style>
                        * {font-family: "Gilroy", "Noto Sans KR", Malgun Gothic, "맑은 고딕", Dotum, "돋움", sans-serif;}
                        .gjs-heading{display: none;}
                        .fit_guide{width: 641px; padding: 40px 40px 60px; margin: 0 auto;}
                        @media screen and (max-width: 590px) {
                            .fit_guide {
                                width: 100%;
                                padding: 50px 16px 60px;
                            }
                        }
                        ${totalStyle}
                    </style>
                    <div class="layout_24FW">${totalContent}</div>
                </body>
            </html>
            `
    }


    async convertComponentToHtml(wrapComponent) {
        let headString = '', content = '', style = '';

        if (this.connectedBoardInfoMap[wrapComponent.attributes.attributes['data-id']].type === 'CLIP_SET') {
            const childComponents = wrapComponent.components().models;
            for (const childComponent of childComponents) {
                let componentContentObject = await HTMLToJSON(childComponent.toHTML());
                componentContentObject = this.removeVideoUnusedTags(componentContentObject);
                let componentContent = await JSONToHTML(componentContentObject);
                componentContent = componentContent.replaceAll('<br></br>', '<br/>')
                content += componentContent;
            }
            style += this.getConnectedBoardStyleString(wrapComponent);

        } else {
            let componentContentObject = await HTMLToJSON(wrapComponent.getChildAt(0).toHTML());
            componentContentObject = this.removeVideoUnusedTags(componentContentObject);
            let componentContent = await JSONToHTML(componentContentObject);
            componentContent = componentContent.replaceAll('<br></br>', '<br/>')
            content += componentContent;
            style += this.getConnectedBoardStyleString(wrapComponent);
        }

        if (wrapComponent.getChildAt(0).attributes.type === "iframe") {
            let componentContentObject = await HTMLToJSON(content);
            const urlSplitList = this.connectedBoardInfoMap[wrapComponent.attributes.attributes['data-id']].url.split('.');
            urlSplitList.pop();
            urlSplitList.push('css');
            const cssUrl = encodeURI(urlSplitList.join('.'));
            headString += `<link href="${cssUrl}" rel="stylesheet">`;
            componentContentObject.attributes["style"] = "width: 100%; border: none;";
            content = await JSONToHTML(componentContentObject);
            content = `<div class="inner-iframe-container">${content}</div>`
        }

        return {headString, content, style};
    }

    removeVideoUnusedTags(contentObject) {
        if (contentObject.content) {
            for (const child of contentObject.content) {
                this.removeVideoUnusedTags(child);
            }
        }

        if (contentObject.type === 'video') {
            delete contentObject.attributes['controls'];
        }
        return contentObject
    }


    getConnectedBoardStyleString(component) {
        let style = '';
        let cssRules = [];
        if (this.connectedBoardInfoMap[component.attributes.attributes['data-id']].type === 'CLIP_SET') {
            const childComponents = component.components().models;
            for (const childComponent of childComponents) {
                const childCssRules = this.getOneDepthComponentCssRules(childComponent);
                const styleObject = this.makeStyleObject(childCssRules.map(cssRule => cssRule.toJSON()));
                const componentStyle = toCSS(styleObject);
                style += componentStyle;
            }
        } else {
            cssRules = this.getOneDepthComponentCssRules(component.getChildAt(0));
            const styleObject = this.makeStyleObject(cssRules.map(cssRule => cssRule.toJSON()));
            const componentStyle = toCSS(styleObject);
            style += componentStyle;
        }
        return style;
    }

    /**
     * @typedef {object} SelectorInfo
     * @property {string} selectorType
     * @property {string?} selector
     * @property {number?} selectorIdx
     * @property {string?} parentSelector
     * @property {number?} parentIdx
     */
    /**
     * 셀렉터 클래스 스트링을 가져오는 함수
     * @param {import('grapesjs').Component} component
     * @param {boolean?} isSwiper
     * @return {SelectorInfo}
     */
    getComponentSelector(component, isSwiper) {
        const higherComponent = findParent(component);
        const connectedBoardId = higherComponent.attributes.attributes['data-id'];
        /** @type {SelectorInfo} */
        const result = {
            connectedBoardId,
            selectorType: 'normal',
            selector: undefined,
            selectorIdx: 0,
            parentSelector: undefined,
            parentIdx: 0
        };

        const parentComponent = component.parent();
        if (parentComponent && parentComponent.getClasses().length > 0) {
            result.parentSelector = this.getComponentSelectorString(parentComponent);
            const parentList = higherComponent.find(result.parentSelector);
            result.parentIdx = this.getComponentSelectorIndex(parentComponent, parentList);
        }
        result.selector = this.getComponentSelectorString(component);

        if (result.selector.length === 0 && ['i', 'b', 'u', 'a', 'strike', 'span'].includes(component.attributes.tagName)) {
            const tagComponents = parentComponent.find(component.attributes.tagName);
            result.selectorIdx = this.getComponentSelectorIndex(component, tagComponents);
            result.selectorType = 'textTag';
            result.selector = component.attributes.tagName;
            return result;
        }
        if (isSwiper) {
            result.selectorType = 'swiper';
            const selectorList = result.selector.split('.');
            let modifiedInEditor = false;
            const newSelectorList = [];
            for (const className of selectorList) {
                if (['on', 'pc', 'mo', ''].includes(className)) continue;
                if (className.startsWith('DeRcN')) {
                    modifiedInEditor = true;
                    continue
                }
                if (className.startsWith('XeRqh') && modifiedInEditor) {
                    modifiedInEditor = false;
                    continue
                }
                newSelectorList.push(`.${className}`);
            }
            const components = parentComponent.find(newSelectorList.join(''));
            result.selectorIdx = this.getComponentSelectorIndex(component, components);
            return result;
        }
        let components = parentComponent && parentComponent.getClasses().length > 0 ? parentComponent.find(result.selector) : higherComponent.find(result.selector);
        // const components = parentComponent && parentComponent.getClasses().length > 0 ? parentComponent.find(result.selector) : higherComponent.find(result.selector);
        if (parentComponent && parentComponent.getClasses().length > 0 && parentComponent.components().models.length === 1) {
            components = parentComponent.components().models;
        }


        result.selectorIdx = this.getComponentSelectorIndex(component, components);
        return result;
    }

    /**
     * 엘리먼트 클래스 셀렉터 스트링을 가져오는 함수
     * @param {import('grapesjs').Component} component
     */
    getComponentSelectorString(component) {
        const classNameList = component.getClasses();
        // if (cla)
        const selectorList = [];
        for (/** @type {string} */ const className of classNameList) {
            if (['on', 'pc', 'mo'].includes(className)) continue;
            selectorList.push(`.${className}`);
        }
        return selectorList.join("");
    }

    getComponentSelectorIndex(component, targetList) {
        let index = 0;
        for (let i = 0; i < targetList.length; i++) {
            if (component === targetList[i]) {
                index = i;
                break;
            }
        }
        return index;
    }

    async setEditor(value) {
        this.editor = value;
        this.fingerprint = await this.getFingerprint();
    }

    /**
     * 현재 페이지가 상품 연결이 된 경우 상품 번호를 세팅해주는 함수
     * @param {string} productNo 상품번호
     */
    setPageProductNo(productNo) {
        this.pageInfo.productNo = productNo;
    }

    /**
     * 편집중인 페이지의 상태값을 변환해주는 함수. 셀렉트박스에서 변경될 때마다 호출
     * @param {string} status 페이지의 상태값
     * @return {Promise<void>}
     */
    async setPageStatus(status) {
        this.pageInfo.status = status;
        await axios.patch('/page/change/status', {pageId: this.pageInfo.id, status: status});
    }

    /**
     * 컴포넌트 리스트로부터 html 리스트를 뽑아내는 함수
     * @param {import('grapesjs').Component[]} compList
     * @return {string}
     */
    getContentHtml(compList) {
        let html = '';
        compList.forEach((component) => {
            if (this.connectedBoardInfoMap[component.attributes.attributes['data-id']]?.type === 'CLIP_SET') {
                const childComponents = component.components();
                for (const childComponent of childComponents) {
                    html += childComponent.toHTML();
                }
            } else {
                html += component.getChildAt(0).toHTML();
            }

        })
        return html;
    }

    /**
     * 컴포넌트 리스트로부터 css 스트링을 뽑아내는 함수
     * @param {import('grapesjs').Component[]} compList 컴포넌트 리스트
     * @return {string}
     */
    getContentCss(compList) {
        let css = '';
        compList.forEach((component) => {
            if (this.connectedBoardInfoMap[component.attributes.attributes['data-id']]?.type === 'CLIP_SET') {
                const childComponents = component.components();
                for (const childComponent of childComponents) {
                    const cssRules = this.getOneDepthComponentCssRules(childComponent);
                    for (const cssRule of cssRules) {
                        css += JSON.stringify(cssRule.toJSON())
                    }
                }
            } else {
                const cssRules = this.getOneDepthComponentCssRules(component.getChildAt(0));
                for (const cssRule of cssRules) {
                    css += JSON.stringify(cssRule.toJSON())
                }
            }

        })
        return css;
    }

    /**
     * 페이지에 변경사항이 있었는지 체크하는 함수
     * @param {string} type 에디터의 타입(PAGE or COMMON)
     * @return {boolean}
     */
    checkModifiedOnEditor(type) {
        if (type === 'PAGE') {
            return getUndoManager().undoStacks.length > 0;
        } else {
            return this.commonBoardEditorModal.initHtml !== this.getContentHtml([this.commonBoardEditorModal.component]) ||
                this.commonBoardEditorModal.initCss !== this.getContentCss([this.commonBoardEditorModal.component]);
        }
    }

    removeHighlighter() {
        this.boardTree.forEach(tree => delete tree.data.checked)
        const toolsWrapper = document.getElementById("gjs-cv-tools");
        const removeList = [];
        for (const child of toolsWrapper.children) {
            if (child.id.startsWith('highlighter-')) {
                removeList.push(child);
            }
        }
        removeList.forEach(child => toolsWrapper.removeChild(child));
    }

    relocateHighlighter() {
        const toolsWrapper = document.getElementById("gjs-cv-tools");
        const removeList = [];
        for (const child of toolsWrapper.children) {
            if (child.id.startsWith('highlighter-')) {
                removeList.push(child);
            }
        }
        removeList.forEach(child => toolsWrapper.removeChild(child));
        for (let i = 0; i < this.boardTree.length; i++) {
            if (this.boardTree[i].data['checked'] && this.boardTree[i].data.type !== 'TOGGLE') {
                const rect = this.componentList[i].view.el.getBoundingClientRect();
                toolsWrapper.appendChild(this.makeConnectedBoardHighlighter(this.boardTree[i].data['data-id'], rect));
            }
        }
    }

    makeConnectedBoardHighlighter(connectedBoardId, rect) {
        const div = document.createElement('div');
        div.setAttribute('id', `highlighter-${connectedBoardId}`);
        div.setAttribute('class', 'gjs-tools');
        div.style['top'] = rect.top + 'px';
        div.style['left'] = ((775 - rect.width) / 2) + 'px';
        div.style['width'] = rect.width + 'px';
        div.style['height'] = rect.height + 'px';
        div.style['border'] = "2px solid #C86733";
        div.style['pointer-events'] = "none";
        return div;
    }

    setBoardTree(tree) {
        this.boardTree = tree;
    }

    sortBoardTree(tree) {
        const childNodes = tree.filter((node) => node.parent !== 0);
        const baseNodes = tree.filter(node => node.parent === 0);
        let newTree = [];
        for (let item of baseNodes) {
            newTree.push(item);
            const children = childNodes.filter((node) => node.parent === item.id);
            for (const child of children) {
                newTree.push(child);
            }
        }

        return newTree;
    }

    async addTableColumn(direction) {
        const currentEditor = this.commonBoardEditorModal.state ? this.commonEditor : this.editor;
        const component = currentEditor.getSelected();
        const tableComponent = findCustomCmp(component);
        const prevContent = tableComponent.toHTML();
        const selectorInfo = this.getComponentSelector(tableComponent);
        let newComponentIndex = direction === 'left' ? component.index() : component.index() + 1;
        const htmlObject = await HTMLToJSON(tableComponent.toHTML());
        for (/** @type {object} */ const row of htmlObject.content) {
            const newColumn = JSON.parse(JSON.stringify(row.content[component.index()]));
            newColumn.content = ["Sample"];
            row.content = [...row.content.slice(0, newComponentIndex), newColumn, ...row.content.slice(newComponentIndex, row.content.length)];

        }
        const newContent = await JSONToHTML(htmlObject);
        tableComponent.replaceWith(newContent);
        currentEditor.select(null);
        getUndoManager().add(new TableUpdateStack({...selectorInfo, html: prevContent}, {
            ...selectorInfo,
            html: newContent
        }))
    }

    async addTableRow(direction) {
        const currentEditor = this.commonBoardEditorModal.state ? this.commonEditor : this.editor;
        const component = currentEditor.getSelected();
        const tableComponent = findCustomCmp(component);
        const prevContent = tableComponent.toHTML();
        const selectorInfo = this.getComponentSelector(tableComponent);
        let newComponentIndex = direction === 'top' ? component.parent().index() : component.parent().index() + 1;
        const htmlObject = await HTMLToJSON(tableComponent.toHTML());
        const newRow = JSON.parse(JSON.stringify(htmlObject.content[component.index()]));
        for (const column of newRow.content) {
            column.content = ["Sample"];
        }
        htmlObject.content = [...htmlObject.content.slice(0, newComponentIndex), newRow, ...htmlObject.content.slice(newComponentIndex, htmlObject.content.length)];
        const newContent = await JSONToHTML(htmlObject);
        tableComponent.replaceWith(newContent);
        currentEditor.select(null);
        getUndoManager().add(new TableUpdateStack({...selectorInfo, html: prevContent}, {
            ...selectorInfo,
            html: newContent
        }))
    }

    async deleteTableColumn() {
        const currentEditor = this.commonBoardEditorModal.state ? this.commonEditor : this.editor;
        const component = currentEditor.getSelected();
        const tableComponent = findCustomCmp(component);
        const prevContent = tableComponent.toHTML();
        const selectorInfo = this.getComponentSelector(tableComponent);
        const componentIndex = component.index();
        const htmlObject = await HTMLToJSON(tableComponent.toHTML());
        for (/** @type {object} */ const row of htmlObject.content) {
            row.content.splice(componentIndex, 1);
        }
        const newContent = await JSONToHTML(htmlObject);
        tableComponent.replaceWith(newContent);
        currentEditor.select(null);
        getUndoManager().add(new TableUpdateStack({...selectorInfo, html: prevContent}, {
            ...selectorInfo,
            html: newContent
        }))
    }

    async deleteTableRow() {
        const currentEditor = this.commonBoardEditorModal.state ? this.commonEditor : this.editor;
        const component = currentEditor.getSelected();
        const tableComponent = findCustomCmp(component);
        const prevContent = tableComponent.toHTML();
        const selectorInfo = this.getComponentSelector(tableComponent);
        const componentIndex = component.parent().index();
        const htmlObject = await HTMLToJSON(tableComponent.toHTML());
        htmlObject.content.splice(componentIndex, 1);
        const newContent = await JSONToHTML(htmlObject);
        tableComponent.replaceWith(newContent);
        currentEditor.select(null);
        getUndoManager().add(new TableUpdateStack({...selectorInfo, html: prevContent}, {
            ...selectorInfo,
            html: newContent
        }))
    }

    async convertImageToLink() {
        const currentEditor = this.commonBoardEditorModal.state ? this.commonEditor : this.editor;
        const component = currentEditor.getSelected();
        const higherComponent = findParent(component);
        const tree = this.boardTree.find(node => node.data['data-id'] === higherComponent.attributes.attributes['data-id']);
        const selectorInfo = this.getComponentSelector(component);
        const prevContent = component.toHTML();
        const createdId = createId(6);
        const newContent = `<a href="#" class="converted_link XeRqh${createdId}">${prevContent}</a>`;
        component.replaceWith(newContent);
        currentEditor.select(null);
        currentEditor.StyleManager.select(null);
        !this.commonBoardEditorModal.state && delete tree.data.checked;
        getUndoManager().add(new ConvertImageStack({
            ...selectorInfo,
            html: prevContent
        }, {
            ...selectorInfo,
            html: newContent
        }));
    }

    async convertLinkToImage() {
        const currentEditor = this.commonBoardEditorModal.state ? this.commonEditor : this.editor;
        let component = currentEditor.getSelected();
        const componentHtml = component.toHTML();
        const parentComponent = component.parent();
        if (parentComponent.attributes.tagName === 'a') {
            component = parentComponent;
        }
        component.replaceWith(componentHtml);
        currentEditor.select(null)
        currentEditor.StyleManager.select(null);
    }

    getOneDepthComponentCssRules(component) {
        if (!component) return [];
        let editor = this.editor;
        if (this.commonBoardEditorModal.state) {
            editor = this.commonEditor;
        }
        const cssRules = editor.CssComposer.getRules().filter(rule => this.getComponentSelectorString(component).indexOf(rule.attributes['component']) > -1 && Object.keys(rule.attributes.style).length > 0);
        return cssRules.sort((a, b) => {
            const aMedia = a.get('mediaText') || '';
            const bMedia = b.get('mediaText') || '';
            return aMedia.length - bMedia.length;
        })
    }

    /**
     * 컴포넌트로부터 CssRule 리스트를 가져오기 위한 함수
     * @param {import('grapesjs').Component} component
     * @return {import('grapesjs').CssRule[]}
     */
    getComponentCssRules = (component) => {
        const selectorString = this.getComponentSelectorString(component);
        const higherComponent = findOneDepthParent(component);
        /** @type {import('grapesjs').CssRule[]} */
        let styles = [];
        // if (selectorString && selectorString.length > 0) {
        //     styles = styles.concat(this.editor.CssComposer.getRules(selectorString));
        // }
        // let classStyle = this.editor.CssComposer.getRule(selectorString);
        // if (classStyle && !styles.includes(classStyle)) {
        //     styles.push(classStyle);
        // }
        // let mediaClassStyle = this.editor.CssComposer.getRule(selectorString, {
        //     atRuleType: 'media',
        //     atRuleParams: '(min-width: 660px)'
        // });
        // if (mediaClassStyle && !styles.includes(mediaClassStyle)) {
        //     styles.push(mediaClassStyle);
        // }
        // const idStyles = this.editor.CssComposer.getRules(`#${component.getId()}`);
        // idStyles.forEach((style) => {
        //     if (!styles.includes(style)) {
        //         styles.push(style);
        //     }
        // });
        // const idStyle = this.editor.CssComposer.getIdRule(`#${component.getId()}`);
        // if (idStyle && !styles.includes(idStyle)) {
        //     styles.push(idStyle);
        // }
        const allCssRules = this.getOneDepthComponentCssRules(higherComponent);
        for (const cssRule of allCssRules) {
            if (cssRule.attributes.selectorsAdd && cssRule.attributes.selectorsAdd.length > 0) {
                const splitSelectorsAddList = cssRule.attributes.selectorsAdd.split(" ");
                if (splitSelectorsAddList[splitSelectorsAddList.length - 1].indexOf(selectorString) > -1) {
                    if (!styles.includes(cssRule)) {
                        styles.push(cssRule);
                    }
                }
            } else {
                const cssSelectorString = cssRule.getSelectorsString();
                if (cssSelectorString === selectorString) {
                    styles.push(cssRule);
                }
            }
        }
        return styles;
    }

    /**
     *
     * @param {import('grapesjs').Component} component
     * @param {String} device
     * @return {{Desktop: import('grapesjs').CssRule[], Mobile: import('grapesjs').CssRule[]}|null}
     */
    getComponentCssRuleWithDevice(component, device) {
        const cssRules = this.getComponentCssRules(component);
        const cssRulesMap = {
            Desktop: [], Mobile: []
        }
        for (const cssRule of cssRules) {
            if (cssRule.attributes.mediaText && cssRule.attributes.mediaText.length > 0) {
                cssRulesMap.Mobile.push(cssRule);
            } else {
                cssRulesMap.Desktop.push(cssRule);
            }
        }
        if (device === 'Mobile' && cssRulesMap.Mobile.length > 0) {
            return cssRulesMap.Mobile[0];
        } else if (cssRulesMap.Desktop.length > 0) {
            return cssRulesMap.Desktop[0];
        } else {
            return null;
        }
    }

    /**
     * 에디터에 스타일 스트링을 추가하면서 컴포넌트에 연결시켜주는 함수
     * @param {string} styleString
     * @param {import('grapesjs').Component|import('grapesjs').Component[]} component
     * @return {import('grapesjs').CssRule[]}
     */
    addStyle(styleString, component) {
        try {
            let editor = this.editor;
            if (this.commonBoardEditorModal.state) {
                editor = this.commonEditor;
            }
            const cssRules = editor ? editor.addStyle(styleString) : this.editor.addStyle(styleString);
            cssRules.forEach(cssRule => {
                cssRule.attributes['component'] = this.getComponentSelectorString(component);
            });
            return cssRules;
        } catch (e) {
            console.log(e);
        }
    }

    setSelectedLink(link) {
        this.selectedLink = link;
    }

    setCaptureLoad(type) {
        this.isScreenshot = type;
    }

    removeAsset(removedAsset) {
        this.pageInfo.assets = this.pageInfo.assets.filter(asset => asset !== removedAsset);
    }

    convertChainActive(component) {
        const chainModels = component.parent().components().models;
        for (const chain of chainModels) {
            chain.removeClass('on');
            if (component.view.el.attributes['connected-comp-id'] && component.view.el.attributes['connected-comp-id'].value) {
                const connectedComponent = this.editor.DomComponents.getById(component.view.el.attributes['connected-comp-id'].value);
                this.convertChainActive(connectedComponent);
            }
            if (component && component.view.el.attributes['obj-img-id'] && component.view.el.attributes['obj-img-id'].value) {
                const objImgIdList = component.view.el.attributes['obj-img-id'].value.split(',');
                for (const objImgId of objImgIdList) {
                    const objImgComponent = this.editor.DomComponents.getById(objImgId);
                    this.convertChainActive(objImgComponent);
                }
            }

        }
        component.addClass('on')
    }
}

export const useAppEditorStore = () => {
    return useStore().appEditorStore;
}

export const getAppEditorStore = () => {
    return getStore().appEditorStore;
}

export const useTransitionEnd = () => {
    const [id, setId] = useState(0);
    const appEditor = useStore().appEditorStore;
    const {transitionTime} = appEditor;

    useEffect(() => {
        setTimeout(() => setId(id => ++id), transitionTime);
    }, [transitionTime]);

    return [id];
}